import React from "react";
import { App as AntdApp, Badge, Button, Dropdown, Input, Modal, Segmented, Space, Spin, Switch, Tabs, Tag, Typography, Tooltip, message, Flex } from "antd";
import { ArrowLeftOutlined, BranchesOutlined, CheckCircleTwoTone, HourglassTwoTone, ReloadOutlined, SaveOutlined, FileImageOutlined, DownloadOutlined, CloudUploadOutlined, EditOutlined, FileExcelOutlined, FormOutlined, DisconnectOutlined } from "@ant-design/icons";
import api from "@/api/axios.js";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import "./FlyerManagePage.css";
import FlyerCardEditor from "../components/FlyerCardEditor.jsx";
import FlyerBulkSelectorModal from "../components/FlyerBulkSelectorModal.jsx";
import UploadRefsModal from "../components/UploadRefsModal.jsx";
import UploadExcelCodesModal from "../components/UploadExcelCodesModal.jsx";
import UploadPdfModal from "../components/UploadPdfModal.jsx";
import EditFlyerModal from "../components/EditFlyerModal.jsx";
import { useSSE } from "@modules/realtime/hooks/useSSE.js";
import icon from "@/assets/imgs/logo-icon.png";

// Import da moduli centralizzati
import { MULTI_AI_FIELDS } from "../constants/fields.js";
import { normalizeIdArray, normalizeCard, normalizeAiValue, isBannerCard } from "../utils/normalizers.js";
import { sanitizeFileName, renameFileWithLabel } from "../utils/formatters.js";
import { triggerBrowserDownload } from "../utils/download.js";
import { useProgressiveList } from "../hooks/useProgressiveList.js";
import { listFieldRegistry } from "@modules/fields/api/fieldRegistryApi.js";
import { normalizeFieldKey } from "@modules/fields/utils/fieldRegistry.js";
import { toHexColor } from "@modules/fields/utils/fieldColors.js";

const { Title, Text } = Typography;
const formatFlyerDate = (value) => {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return date.toLocaleDateString("it-IT");
};

// Vista della barra in alto → campo prodotto da cui dipende.
const TAXONOMY_VIEW_FIELD_KEYS = {
	categories: "categoria",
	classes: "classe",
	funds: "fondi",
	badges: "bollino",
	filters: "filtro",
	mechanics: "meccanica"
};

export default function FlyerManagePage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { flyerId, page: pageParam } = useParams();
	const { message: toast, modal } = AntdApp.useApp?.() || { message, modal: Modal };

	const [rows, setRows] = React.useState([]);
	const rowsRef = React.useRef(rows);
	React.useEffect(() => { rowsRef.current = rows; }, [rows]);
	const [loading, setLoading] = React.useState(false);
	const [saving, setSaving] = React.useState({});
	const [dirty, setDirty] = React.useState({});
	const [pendingRemovals, setPendingRemovals] = React.useState({});
	const [pendingUploads, setPendingUploads] = React.useState({});
	const pendingUploadsRef = React.useRef(pendingUploads);
	const [bulkModalOpen, setBulkModalOpen] = React.useState(false);
	const [bulkApplying, setBulkApplying] = React.useState(false);
	const [refsModalOpen, setRefsModalOpen] = React.useState(false);
	const [excelModalOpen, setExcelModalOpen] = React.useState(false);
	const [pdfModalOpen, setPdfModalOpen] = React.useState(false);
	const [editModalOpen, setEditModalOpen] = React.useState(false);
	const [flyerData, setFlyerData] = React.useState(null);
	const [aiProcessing, setAiProcessing] = React.useState(false);
	const [unlockingProcessing, setUnlockingProcessing] = React.useState(false);
	const [ocrProgress, setOcrProgress] = React.useState(0);
	// Seriali (= zone.id) delle card in rielaborazione AI mirata: spinner/stato per-card.
	const [processingSerials, setProcessingSerials] = React.useState(() => new Set());
	const [showIncompleteCards, setShowIncompleteCards] = React.useState(false);
	const [viewMode, setViewMode] = React.useState("pages");           // "pages" | "categories" | "classes" | "funds" | "badges" | "filters" | "mechanics"
	const [selectedCategory, setSelectedCategory] = React.useState("tutte");
	const [selectedClass, setSelectedClass] = React.useState("tutte");
	const [selectedFund, setSelectedFund] = React.useState("tutte");
	const [selectedBadge, setSelectedBadge] = React.useState("tutti");
	const [selectedFilter, setSelectedFilter] = React.useState("tutti");
	const [selectedMechanic, setSelectedMechanic] = React.useState("tutte");
	const [categories, setCategories] = React.useState(() => [
		{ id: "nessuna", name: "Non assegnate", count: 0 },
		{ id: "tutte", name: "Tutte", count: 0 }
	]);
	const [classFilters, setClassFilters] = React.useState(() => [
		{ id: "nessuna", name: "Non assegnate", count: 0 },
		{ id: "tutte", name: "Tutte", count: 0 }
	]);
	const [fundFilters, setFundFilters] = React.useState(() => [
		{ id: "nessuna", name: "Non assegnate", count: 0 },
		{ id: "tutte", name: "Tutte", count: 0 }
	]);
	const [badgeFilters, setBadgeFilters] = React.useState(() => [
		{ id: "nessuna", name: "Non assegnate", count: 0 },
		{ id: "tutti", name: "Tutti", count: 0 }
	]);
	const [filterFilters, setFilterFilters] = React.useState(() => [
		{ id: "nessuna", name: "Non assegnate", count: 0 },
		{ id: "tutti", name: "Tutti", count: 0 }
	]);
	const [mechanicFilters, setMechanicFilters] = React.useState(() => [
		{ id: "nessuna", name: "Non assegnate", count: 0 },
		{ id: "tutte", name: "Tutte", count: 0 }
	]);

	const [categoryOptions, setCategoryOptions] = React.useState([]);
	const [categoryLoading, setCategoryLoading] = React.useState(false);
	const [cooperativaId, setCooperativaId] = React.useState(null);
	const [classOptions, setClassOptions] = React.useState([]);
	const [classLoading, setClassLoading] = React.useState(false);
	const [fundOptions, setFundOptions] = React.useState([]);
	const [fundLoading, setFundLoading] = React.useState(false);
	const [badgeOptions, setBadgeOptions] = React.useState([]);
	const [badgeLoading, setBadgeLoading] = React.useState(false);
	const [filterOptions, setFilterOptions] = React.useState([]);
	const [filterLoading, setFilterLoading] = React.useState(false);
	const [mechanicOptions, setMechanicOptions] = React.useState([]);
	const [mechanicLoading, setMechanicLoading] = React.useState(false);
	const [productFields, setProductFields] = React.useState([]);
	const [productFieldsLoading, setProductFieldsLoading] = React.useState(false);
	const [fieldRegistry, setFieldRegistry] = React.useState([]);
	const [fieldRegistryLoading, setFieldRegistryLoading] = React.useState(false);

	const fieldRegistryMap = React.useMemo(() => {
		return new Map((fieldRegistry || []).map((field) => [field.key, field]));
	}, [fieldRegistry]);

	const fieldRegistryNameMap = React.useMemo(() => {
		return new Map((fieldRegistry || []).map((field) => [String(field.name || "").toLowerCase(), field]));
	}, [fieldRegistry]);

	const combinedProductFieldsLoading = productFieldsLoading || fieldRegistryLoading;

	// I filtri delle sei tassonomie storiche (barra in alto) seguono i campi attivi del cliente,
	// come l'editor card: prima comparivano sempre, anche per un campo tolto in /org, e filtravano
	// card su un valore che la card poi non mostrava (es. "Fondi" su Bennet). Senza elenco campi
	// (volantino senza cooperativa) restano tutti visibili come prima.
	const activeProductFieldKeys = React.useMemo(
		() => new Set((productFields || []).map((field) => field.key)),
		[productFields]
	);
	const isTaxonomyViewVisible = React.useCallback((view) => {
		const fieldKey = TAXONOMY_VIEW_FIELD_KEYS[view];
		if (!fieldKey)
			return true;
		if (combinedProductFieldsLoading)
			return false;
		return activeProductFieldKeys.size === 0 || activeProductFieldKeys.has(fieldKey);
	}, [activeProductFieldKeys, combinedProductFieldsLoading]);

	React.useEffect(() => {
		if (!combinedProductFieldsLoading && !isTaxonomyViewVisible(viewMode))
			setViewMode("pages");
	}, [combinedProductFieldsLoading, isTaxonomyViewVisible, viewMode]);
	const allCategoryOptions = React.useMemo(() => {
		const map = new Map();
		categoryOptions.forEach((opt) => map.set(String(opt.value), opt));
		rows.forEach((row) => {
			const value = row?.ai?.categoria;
			if (!value) return;
			const key = String(value);
			if (map.has(key)) return;
			const name = String(
				row?.ai?.categoriaNome ||
				row?.ai?.categoriaLabel ||
				row?.ai?.categoriaName ||
				key
			);
			map.set(key, {
				value: key,
				name,
				imagePath: row?.ai?.categoriaImagePath || null,
				imageUrl: row?.ai?.categoriaImageUrl || null
			});
		});
		return [...map.values()];
	}, [categoryOptions, rows]);
	const allClassOptions = React.useMemo(() => {
		const map = new Map();
		classOptions.forEach((opt) => map.set(String(opt.value), opt));
		rows.forEach((row) => {
			const value = row?.ai?.classe;
			if (!value) return;
			const key = String(value);
			if (map.has(key)) return;
			const name = String(
				row?.ai?.classeNome ||
				row?.ai?.classeLabel ||
				row?.ai?.classeName ||
				row?.ai?.classe ||
				key
			);
			map.set(key, {
				value: key,
				name,
				imagePath: row?.ai?.classeImagePath || null,
				imageUrl: row?.ai?.classeImageUrl || null,
				description: row?.ai?.classeDescrizione || ""
			});
		});
		return [...map.values()];
	}, [classOptions, rows]);
	const allFundOptions = React.useMemo(() => {
		const map = new Map();
		fundOptions.forEach((opt) => map.set(String(opt.value), opt));
		rows.forEach((row) => {
			const value = row?.ai?.fondi;
			if (!value) return;
			const key = String(value);
			if (map.has(key)) return;
			const name = String(
				row?.ai?.fondiNome ||
				row?.ai?.fondiLabel ||
				row?.ai?.fondiName ||
				row?.ai?.fondi ||
				key
			);
			map.set(key, {
				value: key,
				name,
				imagePath: row?.ai?.fondiImagePath || null,
				imageUrl: row?.ai?.fondiImageUrl || null,
				description: row?.ai?.fondiDescrizione || ""
			});
		});
		return [...map.values()];
	}, [fundOptions, rows]);
	const allBadgeOptions = React.useMemo(() => {
		const map = new Map();
		badgeOptions.forEach((opt) => map.set(String(opt.value), opt));
		rows.forEach((row) => {
			const values = normalizeIdArray(row?.ai?.bollino);
			if (!values.length) return;
			values.forEach((value) => {
				const key = String(value);
				if (map.has(key)) return;
				// Non dovrebbe più accadere in condizioni normali: flyer.badge.created tiene
				// sincronizzato il catalogo in tempo reale. Placeholder onesto come ultima rete
				// di sicurezza, invece di esporre l'ObjectId grezzo nella Select.
				map.set(key, {
					value: key,
					name: "Bollino non sincronizzato",
					imagePath: null,
					imageUrl: null,
					description: ""
				});
			});
		});
		return [...map.values()];
	}, [badgeOptions, rows]);
	const allFilterOptions = React.useMemo(() => {
		const map = new Map();
		filterOptions.forEach((opt) => map.set(String(opt.value), opt));
		rows.forEach((row) => {
			const values = normalizeIdArray(row?.ai?.filtro);
			if (!values.length) return;
			values.forEach((value) => {
				const key = String(value);
				if (map.has(key)) return;
				const name = String(
					row?.ai?.filtroNome ||
					row?.ai?.filtroLabel ||
					row?.ai?.filtroName ||
					key
				);
				map.set(key, {
					value: key,
					name,
					imagePath: row?.ai?.filtroImagePath || null,
					imageUrl: row?.ai?.filtroImageUrl || null,
					description: row?.ai?.filtroDescrizione || ""
				});
			});
		});
		return [...map.values()];
	}, [filterOptions, rows]);
	const allMechanicOptions = React.useMemo(() => {
		const map = new Map();
		mechanicOptions.forEach((opt) => map.set(String(opt.value), opt));
		rows.forEach((row) => {
			const values = normalizeIdArray(row?.ai?.meccanica);
			if (!values.length) return;
			values.forEach((value) => {
				const key = String(value);
				if (map.has(key)) return;
				// Non dovrebbe più accadere in condizioni normali: flyer.mechanic.created tiene
				// sincronizzato il catalogo in tempo reale. Placeholder onesto come ultima rete
				// di sicurezza, invece di esporre l'ObjectId grezzo nella Select.
				map.set(key, {
					value: key,
					name: "Meccanica non sincronizzata",
					imagePath: null,
					imageUrl: null,
					description: ""
				});
			});
		});
		return [...map.values()];
	}, [mechanicOptions, rows]);

	const [statusFilter, setStatusFilter] = React.useState("all");
	const [query, setQuery] = React.useState("");
	const [flyerName, setFlyerName] = React.useState(() => location.state?.flyerName || "");
	const sellOutLabel = React.useMemo(() => {
		const from = formatFlyerDate(flyerData?.sellOutFrom);
		const to = formatFlyerDate(flyerData?.sellOutTo);
		if (!from && !to) return null;
		if (from && to) return `${from} → ${to}`;
		return from || to;
	}, [flyerData?.sellOutFrom, flyerData?.sellOutTo]);

	const handleFlyerUpdated = (updated) => {
		if (!updated) return;
		setFlyerData((prev) => ({ ...(prev || {}), ...updated }));
		if (updated.name) setFlyerName(updated.name);
		if (updated.cooperativaId !== undefined)
			setCooperativaId(updated.cooperativaId ? String(updated.cooperativaId) : null);
	};

	const handleRefsCountChange = React.useCallback((count) => {
		const nextCount = Number.isFinite(count) ? count : 0;
		setFlyerData((prev) => {
			if (!prev) return prev;
			const currentCount = Number.isFinite(prev.refsCount) ? prev.refsCount : 0;
			if (currentCount === nextCount) return prev;
			return { ...prev, refsCount: nextCount };
		});
	}, []);

	const syncFlyerProcessingUi = React.useCallback((flyer) => {
		const isActive = flyer?.status === "PROCESSING" && !flyer?.isProcessingStale;
		setAiProcessing(isActive);
		if (isActive) {
			const progress = Number(flyer?.processingProgress) || 0;
			setOcrProgress(progress > 0 ? progress : 5);
			return;
		}

		if (flyer?.status === "ERROR" || flyer?.status === "READY" || flyer?.status === "NEW")
			setOcrProgress(0);
	}, []);

	const loadFlyer = React.useCallback(async () => {
		if (!flyerId) return null;
		const { data: fData } = await api.get(`/api/flyers/${flyerId}`);
		const flyer = fData?.flyer || fData;
		setFlyerData(flyer);
		syncFlyerProcessingUi(flyer);
		return flyer;
	}, [flyerId, syncFlyerProcessingUi]);

	const loadCards = React.useCallback(async () => {
		if (!flyerId)
			return [];

		const query = showIncompleteCards ? "?includeIncomplete=1" : "";
		const { data } = await api.get(`/api/flyers/${flyerId}/cards${query}`);
		const list = Array.isArray(data) ? data : data?.items || [];
		const normalized = list.map(normalizeCard);
		setRows(normalized);
		return normalized;
	}, [flyerId, showIncompleteCards]);

	const API = (import.meta.env.VITE_API_URL || "").replace(/\/+$/, "");
	const imgUrl = (rel, updatedAt) => {
		const base = `${API}/media/flyers/${String(rel).replace(/^\/+/, "")}`;
		if (updatedAt) return `${base}?t=${new Date(updatedAt).getTime()}`;
		return base;
	};
	const resolveCategoryImage = React.useCallback((path) => {
		if (!path) return null;
		const str = String(path);
		if (/^https?:\/\//i.test(str)) return str;
		if (!API) return str.replace(/^\/+/, "");
		return `${API}/${str.replace(/^\/+/, "")}`;
	}, [API]);

	// Il catalogo bollini/meccaniche si carica una sola volta all'apertura pagina; se l'AI ne
	// crea uno nuovo al volo durante l'elaborazione (vedi flyer.badge.created/flyer.mechanic.created),
	// va aggiunto qui in tempo reale — altrimenti la card lo referenzia con un ID che la Select
	// non sa risolvere e mostra l'ObjectId grezzo al posto del nome.
	const mergeTaxonomyOption = React.useCallback((setter, item) => {
		if (!item?.id) return;
		setter((prev) => {
			const id = String(item.id);
			if (prev.some((opt) => opt.value === id)) return prev;
			const imagePathPositive = item.imagePathPositive || item.imagePath || null;
			const imagePathNegative = item.imagePathNegative || null;
			const next = [...prev, {
				value: id,
				name: item.name,
				imagePath: imagePathPositive,
				imagePathPositive,
				imagePathNegative,
				imageUrl: resolveCategoryImage(imagePathPositive),
				imageUrlPositive: resolveCategoryImage(imagePathPositive),
				imageUrlNegative: resolveCategoryImage(imagePathNegative),
				description: item.description || ""
			}];
			return next.sort((a, b) => a.name.localeCompare(b.name));
		});
	}, [resolveCategoryImage]);

	// Load cards e verifica status elaborazione
	React.useEffect(() => {
		if (!flyerId) return;
		(async () => {
			setLoading(true);
			try {
				await loadCards();
				await loadFlyer();
			}
			finally {
				setLoading(false);
			}
		})();
	}, [flyerId, loadCards, loadFlyer]);

	// Rielabora con AI la singola card (forza la riestrazione anche se già completata).
	const handleReprocessCard = React.useCallback(async (row) => {
		if (!flyerId || row?.seriale == null) return;
		if (aiProcessing) {
			toast.warning?.("Elaborazione AI già in corso: attendi il completamento.");
			return;
		}
		setProcessingSerials((prev) => { const next = new Set(prev); next.add(row.seriale); return next; });
		setAiProcessing(true);
		setOcrProgress((prev) => (prev > 0 ? prev : 5));
		try {
			await api.post("/api/ai/processing-preprocess", { flyerId, serials: [row.seriale], forceReprocess: true });
			toast.success?.("Rielaborazione AI avviata");
		} catch (err) {
			setProcessingSerials((prev) => { const next = new Set(prev); next.delete(row.seriale); return next; });
			setAiProcessing(false);
			setOcrProgress(0);
			toast.error?.(err?.response?.data?.error || "Errore durante la rielaborazione AI.");
		}
	}, [flyerId, aiProcessing, toast]);

	// SSE per aggiornamenti realtime delle cards
	useSSE(flyerId ? `flyer:${flyerId}` : null, {
		"flyer.card.processing": (p) => {
			if (p?.seriale != null)
				setProcessingSerials((prev) => { const next = new Set(prev); next.add(p.seriale); return next; });
		},
		"flyer.card.error": (p) => {
			if (p?.seriale != null)
				setProcessingSerials((prev) => { const next = new Set(prev); next.delete(p.seriale); return next; });
		},
		"flyer.badge.created": (p) => mergeTaxonomyOption(setBadgeOptions, p?.item),
		"flyer.mechanic.created": (p) => mergeTaxonomyOption(setMechanicOptions, p?.item),
		"flyer.card.created": (p) => {
			if (!p?.card) return;
			if (p.card.seriale != null)
				setProcessingSerials((prev) => { const next = new Set(prev); next.delete(p.card.seriale); return next; });
			setRows((prev) => {
				// Evita duplicati
				const exists = prev.some((r) => r._id === p.card._id);
				if (exists) {
					return prev.map((r) => r._id === p.card._id ? normalizeCard(p.card) : r);
				}
				return [...prev, normalizeCard(p.card)];
			});
		},
		"flyer.card.updated": (p) => {
			if (!p?.card) return;
			const updated = normalizeCard(p.card);
			setRows((prev) => prev.map((r) => r._id === updated._id ? { ...r, ...updated } : r));
		},
		"flyer.card.deleted": (p) => {
			if (!p?.cardId) return;
			setRows((prev) => prev.filter((r) => r._id !== p.cardId));
			setDirty((prev) => {
				if (!prev[p.cardId]) return prev;
				const next = { ...prev };
				delete next[p.cardId];
				return next;
			});
			setPendingUploads((prev) => {
				if (!prev[p.cardId]) return prev;
				const next = { ...prev };
				delete next[p.cardId];
				return next;
			});
		},
		"flyer.variant.start": () => {
			setAiProcessing(true);
			setOcrProgress(5);
		},
		"flyer.status.changed": (p) => {
			setFlyerData(prev => prev ? { ...prev, ...p } : p);
			if (p?.status === "PROCESSING" && !p?.isProcessingStale) {
				setAiProcessing(true);
				setOcrProgress(p?.processingProgress > 0 ? p.processingProgress : 5);
				return;
			}

			setAiProcessing(false);
			if (p?.status === "ERROR" || p?.status === "READY" || p?.status === "NEW")
				setOcrProgress(0);
		},
		"flyer.ocr.progress": (p) => {
			setOcrProgress(p?.percent || 0);
		},
		"flyer.done": (p) => {
			console.log("Flyer elaborazione completata:", p);
			setAiProcessing(false);
			setOcrProgress(100);
			toast.success?.("Elaborazione AI completata!") || message.success("Elaborazione AI completata!");
			// Reset progress dopo un po'
			setTimeout(() => setOcrProgress(0), 3000);
		},
		"flyer.refs.updated": (p) => {
			setFlyerData(prev => prev ? { ...prev, refsCount: p.count } : null);
		},
		"flyer.error": () => {
			setAiProcessing(false);
			setOcrProgress(0);
		}
	});

	const flyerNameFromState = location.state?.flyerName;

	// Safety-net: a elaborazione conclusa azzera gli spinner per-card (nessuna card "appesa").
	React.useEffect(() => { if (!aiProcessing) setProcessingSerials(new Set()); }, [aiProcessing]);

	React.useEffect(() => {
		if (flyerNameFromState) setFlyerName(flyerNameFromState);
	}, [flyerNameFromState]);

	const isProcessingStale = flyerData?.status === "PROCESSING" && flyerData?.isProcessingStale;
	const canUnlockProcessing = isProcessingStale || flyerData?.status === "ERROR";

	const handleUnlockProcessing = React.useCallback(async () => {
		if (!flyerId) return;
		setUnlockingProcessing(true);
		try {
			const {data} = await api.post(`/api/flyers/${flyerId}/processing/unlock`);
			const flyer = data?.flyer || null;
			if (flyer) {
				setFlyerData(flyer);
				syncFlyerProcessingUi(flyer);
			} else {
				await loadFlyer();
			}
			const successMessage = flyerData?.status === "ERROR" ? "Errore sbloccato" : "Elaborazione AI sbloccata";
			toast.success?.(successMessage) || message.success(successMessage);
		}
		catch (error) {
			toast.error?.(error?.response?.data?.error || "Impossibile sbloccare l'elaborazione") || message.error(error?.response?.data?.error || "Impossibile sbloccare l'elaborazione");
		}
		finally {
			setUnlockingProcessing(false);
		}
	}, [flyerId, loadFlyer, syncFlyerProcessingUi, toast]);

	React.useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				setFieldRegistryLoading(true);
				const result = await listFieldRegistry();
				if (cancelled)
					return;

				if (result.ok !== false)
					setFieldRegistry(Array.isArray(result.fields) ? result.fields : []);
				else
					setFieldRegistry([]);
			}
			catch (error) {
				if (!cancelled)
					setFieldRegistry([]);
			}
			finally {
				if (!cancelled)
					setFieldRegistryLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, []);

	React.useEffect(() => {
		if (!cooperativaId) {
			setCategoryOptions([]);
			setCategoryLoading(false);
			setClassOptions([]);
			setClassLoading(false);
			setBadgeOptions([]);
			setBadgeLoading(false);
			setFilterOptions([]);
			setFilterLoading(false);
			setMechanicOptions([]);
			setMechanicLoading(false);
			return;
		}
		let cancelled = false;
		setCategoryLoading(true);
		setCategoryOptions([]);
		(async () => {
			try {
				const { data } = await api.get("/api/categories", { params: { cooperativaId } });
				if (cancelled) return;
				const items = Array.isArray(data?.items) ? data.items : [];
				const normalized = items.map((item) => {
					const id = String(item.id || item._id);
					return {
						value: id,
						name: item.name,
						imagePath: item.imagePath || null,
						imageUrl: resolveCategoryImage(item.imagePath),
						description: item.description || ""
					};
				}).sort((a, b) => a.name.localeCompare(b.name));
				setCategoryOptions(normalized);
			}
			catch (error) {
				if (!cancelled)
					setCategoryOptions([]);
			}
			finally {
				if (!cancelled)
					setCategoryLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [cooperativaId, resolveCategoryImage]);

	React.useEffect(() => {
		if (!cooperativaId) {
			setFundOptions([]);
			setFundLoading(false);
			return;
		}
		let cancelled = false;
		setFundLoading(true);
		setFundOptions([]);
		(async () => {
			try {
				const { data } = await api.get("/api/funds", { params: { cooperativaId } });
				if (cancelled) return;
				const items = Array.isArray(data?.items) ? data.items : [];
				const normalized = items.map((item) => {
					const id = String(item.id || item._id);
					return {
						value: id,
						name: item.name,
						imagePath: item.imagePath || null,
						imageUrl: resolveCategoryImage(item.imagePath),
						description: item.description || ""
					};
				}).sort((a, b) => a.name.localeCompare(b.name));
				setFundOptions(normalized);
			}
			catch (error) {
				if (!cancelled)
					setFundOptions([]);
			}
			finally {
				if (!cancelled)
					setFundLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [cooperativaId, resolveCategoryImage]);

	React.useEffect(() => {
		if (!cooperativaId) {
			setClassOptions([]);
			setClassLoading(false);
			return;
		}
		let cancelled = false;
		setClassLoading(true);
		setClassOptions([]);
		(async () => {
			try {
				const { data } = await api.get("/api/classes", { params: { cooperativaId } });
				if (cancelled) return;
				const items = Array.isArray(data?.items) ? data.items : [];
				const normalized = items.map((item) => {
					const id = String(item.id || item._id);
					const imagePathPositive = item.imagePathPositive || item.imagePath || null;
					const imagePathNegative = item.imagePathNegative || null;
					return {
						value: id,
						name: item.name,
						imagePath: imagePathPositive,
						imagePathPositive,
						imagePathNegative,
						imageUrl: resolveCategoryImage(imagePathPositive),
						imageUrlPositive: resolveCategoryImage(imagePathPositive),
						imageUrlNegative: resolveCategoryImage(imagePathNegative),
						description: item.description || ""
					};
				}).sort((a, b) => a.name.localeCompare(b.name));
				setClassOptions(normalized);
			}
			catch (error) {
				if (!cancelled)
					setClassOptions([]);
			}
			finally {
				if (!cancelled)
					setClassLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [cooperativaId, resolveCategoryImage]);

	React.useEffect(() => {
		if (!cooperativaId) {
			setBadgeOptions([]);
			setBadgeLoading(false);
			return;
		}
		let cancelled = false;
		setBadgeLoading(true);
		setBadgeOptions([]);
		(async () => {
			try {
				const { data } = await api.get("/api/badges", { params: { cooperativaId } });
				if (cancelled) return;
				const items = Array.isArray(data?.items) ? data.items : [];
				const normalized = items.map((item) => {
					const id = String(item.id || item._id);
					const imagePathPositive = item.imagePathPositive || item.imagePath || null;
					const imagePathNegative = item.imagePathNegative || null;
					return {
						value: id,
						name: item.name,
						imagePath: imagePathPositive,
						imagePathPositive,
						imagePathNegative,
						imageUrl: resolveCategoryImage(imagePathPositive),
						imageUrlPositive: resolveCategoryImage(imagePathPositive),
						imageUrlNegative: resolveCategoryImage(imagePathNegative),
						description: item.description || ""
					};
				}).sort((a, b) => a.name.localeCompare(b.name));
				setBadgeOptions(normalized);
			}
			catch (error) {
				if (!cancelled)
					setBadgeOptions([]);
			}
			finally {
				if (!cancelled)
					setBadgeLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [cooperativaId, resolveCategoryImage]);

	React.useEffect(() => {
		if (!cooperativaId) {
			setFilterOptions([]);
			setFilterLoading(false);
			return;
		}
		let cancelled = false;
		setFilterLoading(true);
		setFilterOptions([]);
		(async () => {
			try {
				const { data } = await api.get("/api/filters", { params: { cooperativaId } });
				if (cancelled) return;
				const items = Array.isArray(data?.items) ? data.items : [];
				const normalized = items.map((item) => {
					const id = String(item.id || item._id);
					return {
						value: id,
						name: item.name,
						imagePath: item.imagePath || null,
						imageUrl: resolveCategoryImage(item.imagePath),
						description: item.description || ""
					};
				}).sort((a, b) => a.name.localeCompare(b.name));
				setFilterOptions(normalized);
			}
			catch (error) {
				if (!cancelled)
					setFilterOptions([]);
			}
			finally {
				if (!cancelled)
					setFilterLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [cooperativaId]);

	React.useEffect(() => {
		if (!cooperativaId) {
			setMechanicOptions([]);
			setMechanicLoading(false);
			return;
		}
		let cancelled = false;
		setMechanicLoading(true);
		setMechanicOptions([]);
		(async () => {
			try {
				const { data } = await api.get("/api/mechanics", { params: { cooperativaId } });
				if (cancelled) return;
				const items = Array.isArray(data?.items) ? data.items : [];
				const normalized = items.map((item) => {
					const id = String(item.id || item._id);
					const imagePathPositive = item.imagePathPositive || item.imagePath || null;
					const imagePathNegative = item.imagePathNegative || null;
					return {
						value: id,
						name: item.name,
						imagePath: imagePathPositive,
						imagePathPositive,
						imagePathNegative,
						imageUrl: resolveCategoryImage(imagePathPositive),
						imageUrlPositive: resolveCategoryImage(imagePathPositive),
						imageUrlNegative: resolveCategoryImage(imagePathNegative),
						description: item.description || ""
					};
				}).sort((a, b) => a.name.localeCompare(b.name));
				setMechanicOptions(normalized);
			}
			catch (error) {
				if (!cancelled)
					setMechanicOptions([]);
			}
			finally {
				if (!cancelled)
					setMechanicLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [cooperativaId, resolveCategoryImage]);

	// Tassonomie generiche (focus, stile, variante, fondo e — dopo il cutover del Task 15 —
	// banner/banner_collegato): un solo effetto per tutti i campi MULTISELECT o SELECT non
	// legacy, invece di uno stato per campo come per le sei storiche.
	const [genericTaxonomyData, setGenericTaxonomyData] = React.useState({});
	const [genericTaxonomyLoading, setGenericTaxonomyLoading] = React.useState(false);

	const genericFieldKeys = React.useMemo(
		() => fieldRegistry.filter((f) => {
			const type = String(f?.type || "").toUpperCase();
			return (type === "MULTISELECT" || type === "SELECT")
				&& !["bollino", "meccanica", "filtro", "categoria", "classe", "fondi"].includes(f.key);
		}).map((f) => f.key),
		[fieldRegistry]
	);

	React.useEffect(() => {
		if (!cooperativaId || !genericFieldKeys.length) {
			setGenericTaxonomyData({});
			return undefined;
		}

		let cancelled = false;
		setGenericTaxonomyLoading(true);
		Promise.all(genericFieldKeys.map(async (key) => {
			const { data } = await api.get(`/api/taxonomy-values/${key}`, { params: { cooperativaId } });
			const items = Array.isArray(data?.items) ? data.items : [];
			return [key, items.map((it) => ({ value: String(it.id || it._id), label: it.name }))];
		}))
			.then((entries) => { if (!cancelled) setGenericTaxonomyData(Object.fromEntries(entries)); })
			.catch(() => { if (!cancelled) setGenericTaxonomyData({}); })
			.finally(() => { if (!cancelled) setGenericTaxonomyLoading(false); });

		return () => { cancelled = true; };
	}, [cooperativaId, genericFieldKeys]);

	const handleCreateGenericTaxonomyValue = React.useCallback(async (fieldKey, name) => {
		const trimmed = String(name || "").trim();
		if (!trimmed || !cooperativaId)
			return null;
		try {
			const { data } = await api.post(`/api/taxonomy-values/${fieldKey}`, { name: trimmed, cooperativaId });
			const item = data?.item;
			if (!item?.id)
				return null;
			const option = { value: String(item.id), label: item.name };
			setGenericTaxonomyData((prev) => ({
				...prev,
				[fieldKey]: [...(prev[fieldKey] || []), option]
					.sort((a, b) => a.label.localeCompare(b.label, "it", { sensitivity: "base" }))
			}));
			return option;
		}
		catch (error) {
			const errMsg = error?.response?.data?.error || "Impossibile creare il valore";
			toast.error?.(errMsg) || message.error(errMsg);
			return null;
		}
	}, [cooperativaId, toast]);

	const genericTaxonomies = React.useMemo(() => Object.fromEntries(
		genericFieldKeys.map((key) => [key, {
			options: genericTaxonomyData[key] || [],
			loading: genericTaxonomyLoading,
			onCreate: (name) => handleCreateGenericTaxonomyValue(key, name)
		}])
	), [genericFieldKeys, genericTaxonomyData, genericTaxonomyLoading, handleCreateGenericTaxonomyValue]);

	React.useEffect(() => {
		if (!flyerId) return;
		let cancelled = false;
		(async () => {
			try {
				const { data } = await api.get(`/api/flyers/${flyerId}`);
				if (cancelled) return;
				const flyer = data?.flyer || data;
				setCooperativaId(flyer?.cooperativaId ? String(flyer.cooperativaId) : null);
				if (!flyerNameFromState) {
					const name = flyer?.name || flyer?.title || flyer?.label || "Volantino";
					setFlyerName((prev) => prev || name);
				}
			}
			catch (e) {
				if (!flyerNameFromState)
					setFlyerName((prev) => prev || "Volantino");
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [flyerId, flyerNameFromState]);

	React.useEffect(() => {
		if (!cooperativaId) {
			setProductFields([]);
			setProductFieldsLoading(false);
			return;
		}

		let cancelled = false;

		(async () => {
			try {
				setProductFieldsLoading(true);
				const { data } = await api.get("/api/orgnodes");
				if (cancelled)
					return;

				const items = Array.isArray(data?.items) ? data.items : [];
				const target = items.find((item) => String(item?._id) === String(cooperativaId));
				const allowed = Array.isArray(target?.allowedProductFields) ? target.allowedProductFields : [];

				const mapped = allowed.map((field) => {
					const rawName = field?.name || field?.key;
					const key = normalizeFieldKey(rawName);
					if (!key) return null;
					const registryField = fieldRegistryMap.get(key)
						|| fieldRegistryNameMap.get(String(rawName || "").toLowerCase());
					const resolvedKey = registryField?.key || key;
					return {
						key: resolvedKey,
						name: registryField?.name || field?.name || resolvedKey,
						type: registryField?.type || field?.type || "TEXT",
						dateMode: registryField?.dateMode,
						editorColumn: registryField?.editorColumn,
						editorPosition: registryField?.editorPosition
					};
				}).filter(Boolean);

				setProductFields(mapped);
			}
			catch (error) {
				if (cancelled)
					return;

				setProductFields([]);
				toast.error?.("Impossibile caricare i campi prodotto") || message.error("Impossibile caricare i campi prodotto");
			}
			finally {
				if (!cancelled)
					setProductFieldsLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [cooperativaId, toast, fieldRegistryMap, fieldRegistryNameMap]);

	// Group by pagina usando il campo page della card (non dal relPath)
	const pages = React.useMemo(() => {
		const map = new Map();
		for (const r of rows) {
			// Usa il campo page direttamente se presente, altrimenti fallback a parsing del relPath
			let p = r.page;
			if (p == null) {
				const m = String(r.relPath || "").match(/crops\/(\d+)\/[^/]+\.webp$/i);
				p = m ? Number(m[1]) : 0;
			}
			if (!map.has(p)) map.set(p, []);
			map.get(p).push(r);
		}
		// Ordina le card per crop dentro ogni pagina
		const arr = [...map.entries()].sort((a, b) => a[0] - b[0]).map(([page, items]) => ({
			page,
			items: items.sort((a, b) => (a.crop || 0) - (b.crop || 0))
		}));
		return arr;
	}, [rows]);

	const pageNumbers = React.useMemo(() => pages.map(({ page }) => page), [pages]);

	const pendingChangesCount = React.useMemo(() => {
		return Object.keys(dirty).length;
	}, [dirty]);

	const hasPendingChanges = pendingChangesCount > 0;

	// Ricaricare o chiudere la scheda con modifiche non salvate le farebbe perdere senza
	// avviso: il browser chiede conferma. Non copre la navigazione interna all'app.
	const hasUnsavedWork = hasPendingChanges
		|| Object.keys(pendingUploads).length > 0
		|| Object.keys(pendingRemovals).length > 0;

	React.useEffect(() => {
		if (!hasUnsavedWork)
			return undefined;
		const onBeforeUnload = (event) => {
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", onBeforeUnload);
		return () => window.removeEventListener("beforeunload", onBeforeUnload);
	}, [hasUnsavedWork]);

	const categorySelectOptions = React.useMemo(() => {
		return allCategoryOptions.map((opt) => ({
			value: opt.value,
			label: (
				<Space size={8} className="fm-category-option">
					<img src={opt.imageUrl || icon} alt="" className="fm-category-thumb" />
					<span>{opt.name}</span>
				</Space>
			),
			searchValue: opt.name.toLowerCase(),
			raw: opt
		}));
	}, [allCategoryOptions]);
	const classSelectOptions = React.useMemo(() => {
		return allClassOptions.map((opt) => ({
			value: String(opt.value),
			label: (
				<Space size={8} className="fm-category-option">
					<span className="fm-category-thumbs">
						<img src={opt.imageUrlPositive || opt.imageUrl || icon} alt="" className="fm-category-thumb fm-category-thumb--positive" />
						<img src={opt.imageUrlNegative || opt.imageUrlPositive || opt.imageUrl || icon} alt="" className="fm-category-thumb fm-category-thumb--negative" />
					</span>
					<span>{opt.name}</span>
				</Space>
			),
			searchValue: opt.name.toLowerCase(),
			raw: opt
		}));
	}, [allClassOptions]);
	const fundSelectOptions = React.useMemo(() => {
		return allFundOptions.map((opt) => ({
			value: String(opt.value),
			label: (
				<Space size={8} className="fm-category-option">
					<img src={opt.imageUrl || icon} alt="" className="fm-category-thumb" />
					<span>{opt.name}</span>
				</Space>
			),
			searchValue: opt.name.toLowerCase(),
			raw: opt
		}));
	}, [allFundOptions]);
	const badgeSelectOptions = React.useMemo(() => {
		return allBadgeOptions.map((opt) => ({
			value: String(opt.value),
			label: (
				<Space size={8} className="fm-category-option">
					<span className="fm-category-thumbs">
						<img src={opt.imageUrlPositive || opt.imageUrl || icon} alt="" className="fm-category-thumb fm-category-thumb--positive" />
						<img src={opt.imageUrlNegative || opt.imageUrlPositive || opt.imageUrl || icon} alt="" className="fm-category-thumb fm-category-thumb--negative" />
					</span>
					<span>{opt.name}</span>
				</Space>
			),
			searchValue: opt.name.toLowerCase(),
			raw: opt
		}));
	}, [allBadgeOptions]);
	const filterSelectOptions = React.useMemo(() => {
		return allFilterOptions.map((opt) => ({
			value: String(opt.value),
			label: (
				<Space size={8} className="fm-category-option">
					<img src={opt.imageUrl || icon} alt="" className="fm-category-thumb" />
					<span>{opt.name}</span>
				</Space>
			),
			searchValue: opt.name.toLowerCase(),
			raw: opt
		}));
	}, [allFilterOptions]);
	const mechanicSelectOptions = React.useMemo(() => {
		return allMechanicOptions.map((opt) => ({
			value: String(opt.value),
			label: (
				<Space size={8} className="fm-category-option">
					<span className="fm-category-thumbs">
						<img src={opt.imageUrlPositive || opt.imageUrl || icon} alt="" className="fm-category-thumb fm-category-thumb--positive" />
						<img src={opt.imageUrlNegative || opt.imageUrlPositive || opt.imageUrl || icon} alt="" className="fm-category-thumb fm-category-thumb--negative" />
					</span>
					<span>{opt.name}</span>
				</Space>
			),
			searchValue: opt.name.toLowerCase(),
			raw: opt
		}));
	}, [allMechanicOptions]);

	React.useEffect(() => {
		const total = Array.isArray(rows) ? rows.length : 0;
		const counts = new Map();
		let unassigned = 0;
		rows.forEach((row) => {
			const value = row?.ai?.categoria;
			if (!value) {
				unassigned += 1;
				return;
			}
			const key = String(value);
			counts.set(key, (counts.get(key) || 0) + 1);
		});

		const nav = [
			{ id: "nessuna", name: "Non assegnate", count: unassigned },
			{ id: "tutte", name: "Tutte", count: total }
		];
		allCategoryOptions.forEach((opt) => {
			const key = String(opt.value);
			const count = counts.get(key) || 0;
			if (count > 0) {
				nav.push({
					id: key,
					name: opt.name,
					count,
					imagePath: opt.imagePath,
					imageUrl: opt.imageUrl
				});
			}
			counts.delete(key);
		});

		counts.forEach((count, key) => {
			if (count > 0)
				nav.push({ id: key, name: key, count });
		});

		setCategories(nav);
	}, [rows, allCategoryOptions]);

	React.useEffect(() => {
		if (!categories.some((cat) => cat.id === selectedCategory))
			setSelectedCategory("tutte");
	}, [categories, selectedCategory]);

	React.useEffect(() => {
		const total = Array.isArray(rows) ? rows.length : 0;
		const counts = new Map();
		let unassigned = 0;
		rows.forEach((row) => {
			const value = row?.ai?.classe;
			if (!value) {
				unassigned += 1;
				return;
			}
			const key = String(value);
			counts.set(key, (counts.get(key) || 0) + 1);
		});

		const nav = [
			{ id: "nessuna", name: "Non assegnate", count: unassigned },
			{ id: "tutte", name: "Tutte", count: total }
		];
		allClassOptions.forEach((opt) => {
			const key = String(opt.value);
			const count = counts.get(key) || 0;
			if (count > 0) {
				nav.push({
					id: key,
					name: opt.name,
					count,
					description: opt.description || ""
				});
			}
			counts.delete(key);
		});

		counts.forEach((count, key) => {
			if (count > 0)
				nav.push({ id: key, name: key, count });
		});

		setClassFilters(nav);
	}, [rows, allClassOptions]);

	React.useEffect(() => {
		if (!classFilters.some((cls) => cls.id === selectedClass))
			setSelectedClass("tutte");
	}, [classFilters, selectedClass]);

	React.useEffect(() => {
		const total = Array.isArray(rows) ? rows.length : 0;
		const counts = new Map();
		let unassigned = 0;
		rows.forEach((row) => {
			const value = row?.ai?.fondi;
			if (!value) {
				unassigned += 1;
				return;
			}
			const key = String(value);
			counts.set(key, (counts.get(key) || 0) + 1);
		});

		const nav = [
			{ id: "nessuna", name: "Non assegnate", count: unassigned },
			{ id: "tutte", name: "Tutte", count: total }
		];
		allFundOptions.forEach((opt) => {
			const key = String(opt.value);
			const count = counts.get(key) || 0;
			if (count > 0) {
				nav.push({
					id: key,
					name: opt.name,
					count,
					description: opt.description || ""
				});
			}
			counts.delete(key);
		});

		counts.forEach((count, key) => {
			if (count > 0)
				nav.push({ id: key, name: key, count });
		});

		setFundFilters(nav);
	}, [rows, allFundOptions]);

	React.useEffect(() => {
		if (!fundFilters.some((f) => f.id === selectedFund))
			setSelectedFund("tutte");
	}, [fundFilters, selectedFund]);

	React.useEffect(() => {
		const total = Array.isArray(rows) ? rows.length : 0;
		const counts = new Map();
		let unassigned = 0;
		rows.forEach((row) => {
			const values = normalizeIdArray(row?.ai?.bollino);
			if (!values.length) {
				unassigned += 1;
				return;
			}
			values.forEach((value) => {
				const key = String(value);
				counts.set(key, (counts.get(key) || 0) + 1);
			});
		});

		const nav = [
			{ id: "nessuna", name: "Non assegnate", count: unassigned },
			{ id: "tutti", name: "Tutti", count: total }
		];
		allBadgeOptions.forEach((opt) => {
			const key = String(opt.value);
			const count = counts.get(key) || 0;
			if (count > 0) {
				nav.push({
					id: key,
					name: opt.name,
					count,
					imagePath: opt.imagePath,
					imageUrl: opt.imageUrl,
					description: opt.description || ""
				});
			}
			counts.delete(key);
		});

		counts.forEach((count, key) => {
			if (count > 0)
				nav.push({ id: key, name: key, count });
		});

		setBadgeFilters(nav);
	}, [rows, allBadgeOptions]);

	React.useEffect(() => {
		if (!badgeFilters.some((badge) => badge.id === selectedBadge))
			setSelectedBadge("tutti");
	}, [badgeFilters, selectedBadge]);

	React.useEffect(() => {
		const total = Array.isArray(rows) ? rows.length : 0;
		const counts = new Map();
		let unassigned = 0;
		rows.forEach((row) => {
			const values = normalizeIdArray(row?.ai?.filtro);
			if (!values.length) {
				unassigned += 1;
				return;
			}
			values.forEach((value) => {
				const key = String(value);
				counts.set(key, (counts.get(key) || 0) + 1);
			});
		});

		const nav = [
			{ id: "nessuna", name: "Non assegnate", count: unassigned },
			{ id: "tutti", name: "Tutti", count: total }
		];
		allFilterOptions.forEach((opt) => {
			const key = String(opt.value);
			const count = counts.get(key) || 0;
			if (count > 0) {
				nav.push({
					id: key,
					name: opt.name,
					count,
					imagePath: opt.imagePath,
					imageUrl: opt.imageUrl,
					description: opt.description || ""
				});
			}
			counts.delete(key);
		});

		counts.forEach((count, key) => {
			if (count > 0)
				nav.push({ id: key, name: key, count });
		});

		setFilterFilters(nav);
	}, [rows, allFilterOptions]);

	React.useEffect(() => {
		if (!filterFilters.some((filterItem) => filterItem.id === selectedFilter))
			setSelectedFilter("tutti");
	}, [filterFilters, selectedFilter]);

	React.useEffect(() => {
		const total = Array.isArray(rows) ? rows.length : 0;
		const counts = new Map();
		let unassigned = 0;
		rows.forEach((row) => {
			const values = normalizeIdArray(row?.ai?.meccanica);
			if (!values.length) {
				unassigned += 1;
				return;
			}
			values.forEach((value) => {
				const key = String(value);
				counts.set(key, (counts.get(key) || 0) + 1);
			});
		});

		const nav = [
			{ id: "nessuna", name: "Non assegnate", count: unassigned },
			{ id: "tutte", name: "Tutte", count: total }
		];
		allMechanicOptions.forEach((opt) => {
			const key = String(opt.value);
			const count = counts.get(key) || 0;
			if (count > 0) {
				nav.push({
					id: key,
					name: opt.name,
					count,
					imagePath: opt.imagePath,
					imageUrl: opt.imageUrl,
					description: opt.description || ""
				});
			}
			counts.delete(key);
		});

		counts.forEach((count, key) => {
			if (count > 0)
				nav.push({ id: key, name: key, count });
		});

		setMechanicFilters(nav);
	}, [rows, allMechanicOptions]);

	React.useEffect(() => {
		if (!mechanicFilters.some((mechanic) => mechanic.id === selectedMechanic))
			setSelectedMechanic("tutte");
	}, [mechanicFilters, selectedMechanic]);


	const numericPage = React.useMemo(() => {
		if (!pageParam) return null;
		const n = Number(pageParam);
		return Number.isFinite(n) ? n : null;
	}, [pageParam]);

	const activePage = numericPage ?? (pages[0]?.page ?? null);

	React.useEffect(() => {
		if (!flyerId || !pages.length) return;
		if (numericPage != null && pages.some(({ page }) => page === numericPage)) return;
		const fallback = pages[0]?.page;
		if (fallback == null) return;
		// focusCardId sopravvive al reindirizzamento: "Apri card" può arrivare senza pagina.
		navigate(`/flyermanage/${flyerId}/${fallback}`, { replace: true, state: { flyerName, focusCardId: location.state?.focusCardId } });
	}, [flyerId, pages, numericPage, navigate, flyerName, location.state?.focusCardId]);

	const setField = React.useCallback((id, key, value) => {
		if (key.startsWith("banner.")) {
			const field = key.slice("banner.".length);
			const normalizedBannerValue = field === "categoria"
				? (value || null)
				: field === "banner" || field === "banner_video"
					? (value == null ? "" : String(value))
					: value ?? "";
			let removalCandidate = null;
			setRows((prev) => prev.map((r) => {
				if (r._id !== id)
					return r;
				const currentBanner = { ...(r.banner || {}) };
				if (field.startsWith("assets.")) {
					const assetKey = field.slice("assets.".length);
					const assets = { ...(currentBanner.assets || {}) };
					const previousAssetValue = typeof assets[assetKey] === "string" ? assets[assetKey] : "";
					const nextAssetValue = value ?? "";
					const assetType = assetKey === "video"
						? "video"
						: assetKey === "image" || assetKey === "gif"
							? "image"
							: null;
					if (
						assetType &&
						previousAssetValue &&
						previousAssetValue !== nextAssetValue
					) {
						removalCandidate = { type: assetType, path: previousAssetValue };
					}
					assets[assetKey] = nextAssetValue;
					currentBanner.assets = assets;
					return { ...r, banner: currentBanner };
				}
				currentBanner[field] = normalizedBannerValue;
				return { ...r, banner: currentBanner };
			}));
			if (removalCandidate) {
				setPendingRemovals((prev) => {
					const next = { ...prev };
					const entry = { ...(next[id] || {}) };
					entry[removalCandidate.type] = removalCandidate.path;
					next[id] = entry;
					return next;
				});
			}
			const dirtyValue = field.startsWith("assets.") ? (value ?? "") : normalizedBannerValue;
			setDirty((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: dirtyValue } }));
			return;
		}

		const normalized = normalizeAiValue(key, value);
		setRows((prev) => prev.map((r) => (r._id === id ? { ...r, ai: { ...(r.ai || {}), [key]: normalized } } : r)));
		setDirty((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [`ai.${key}`]: normalized } }));
	}, [setRows, setDirty, setPendingRemovals]);

	const handlePendingAssetChange = React.useCallback((cardId, assetType, payload) => {
		if (!cardId || !assetType) return;
		setPendingUploads((prev) => {
			const current = prev[cardId];
			if (payload && current?.[assetType] === payload) {
				pendingUploadsRef.current = prev;
				return prev;
			}
			if (!payload && (!current || !(assetType in current))) {
				pendingUploadsRef.current = prev;
				return prev;
			}
			const next = { ...prev };
			const entry = { ...(current || {}) };
			if (payload) {
				entry[assetType] = payload;
				next[cardId] = entry;
				pendingUploadsRef.current = next;
				return next;
			}
			delete entry[assetType];
			if (Object.keys(entry).length) {
				next[cardId] = entry;
			}
			else {
				delete next[cardId];
			}
			pendingUploadsRef.current = next;
			return next;
		});
	}, []);

	React.useEffect(() => {
		pendingUploadsRef.current = pendingUploads;
	}, [pendingUploads]);

	const clearPendingUploadsForCard = React.useCallback((cardId) => {
		if (!cardId) return;
		setPendingUploads((prev) => {
			if (!prev[cardId]) return prev;
			const next = { ...prev };
			delete next[cardId];
			return next;
		});
	}, []);

	const handleServerCardUpdate = React.useCallback((serverCard) => {
		const normalizedCard = normalizeCard(serverCard);
		const cardId = String(normalizedCard?._id || "");
		if (!cardId)
			return;

		const serverAi = normalizedCard?.ai || {};
		setRows((prev) => prev.map((row) => {
			if (String(row._id) !== cardId)
				return row;
			return {
				...row,
				updatedAt: normalizedCard?.updatedAt || row.updatedAt,
				ai: {
					...(row.ai || {}),
					foto_prodotto_items: Array.isArray(serverAi.foto_prodotto_items) ? serverAi.foto_prodotto_items : [],
					foto_prodotto: serverAi.foto_prodotto || "",
					foto_prodotto_original: serverAi.foto_prodotto_original || null,
					foto_prodotto_composition: serverAi.foto_prodotto_composition ?? null,
					foto_prodotto_canvasConfig: serverAi.foto_prodotto_canvasConfig ?? null,
				}
			};
		}));
		setDirty((prev) => {
			const cardDirty = prev[cardId];
			if (!cardDirty)
				return prev;

			const filtered = Object.entries(cardDirty).reduce((acc, [key, value]) => {
				if (!key.startsWith("ai.foto_prodotto"))
					acc[key] = value;
				return acc;
			}, {});

			if (Object.keys(filtered).length === Object.keys(cardDirty).length)
				return prev;

			const next = { ...prev };
			if (Object.keys(filtered).length)
				next[cardId] = filtered;
			else
				delete next[cardId];
			return next;
		});
	}, [setRows, setDirty]);


	const handleBulkApply = React.useCallback(async ({ ids, updates, additions, appends }) => {
		if (!Array.isArray(ids) || !ids.length) {
			toast.warning?.("Nessun prodotto selezionato") || message.warning("Nessun prodotto selezionato");
			return;
		}
		const updateEntries = Object.entries(updates || {}).filter(([, value]) => value !== undefined);
		const additionEntries = Object.entries(additions || {}).filter(
			([, vals]) => Array.isArray(vals) && vals.length,
		);
		// "Aggiungi in coda" sui campi di testo: { key: { text, separator } }, risolto per-card.
		const appendEntries = Object.entries(appends || {}).filter(
			([, entry]) => typeof entry?.text === "string" && entry.text.trim(),
		);
		const appendText = (current, { text, separator }) => {
			const base = current == null ? "" : String(current);
			return base.trim() ? `${base.replace(/\s+$/, "")}${separator}${text.trim()}` : text.trim();
		};
		if (!updateEntries.length && !additionEntries.length && !appendEntries.length) {
			toast.warning?.("Nessuna modifica da applicare") || message.warning("Nessuna modifica da applicare");
			return;
		}
		setBulkApplying(true);
		try {
			const normalizedIds = ids.map((id) => String(id));
			// Snapshot pre-modifica: serve sia per il merge ottimistico "Aggiungi" sia per
			// calcolare il valore finale da inviare al backend per ciascuna card.
			const rowMap = new Map((rowsRef.current || []).map((r) => [String(r._id), r]));
			const mergeAddition = (current, vals) => {
				const merged = normalizeIdArray(current);
				normalizeIdArray(vals).forEach((v) => {
					if (!merged.includes(v)) merged.push(v);
				});
				return merged;
			};

			// Valori finali per card: { chiave setField → valore }. Un banner tiene i suoi dati
			// in banner.* (scheda banner ed XML leggono solo da lì) e dei campi prodotto ha solo
			// la categoria: gli altri si saltano, altrimenti finirebbero in ai.* senza uscire mai.
			let skippedBanners = 0;
			const valuesById = new Map();
			for (const id of normalizedIds) {
				const row = rowMap.get(id);
				const values = {};
				if (isBannerCard(row)) {
					const categoryEntry = updateEntries.find(([key]) => key === "categoria");
					if (categoryEntry)
						values["banner.categoria"] = categoryEntry[1];
					if (updateEntries.length > (categoryEntry ? 1 : 0) || additionEntries.length || appendEntries.length)
						skippedBanners++;
				}
				else {
					for (const [key, value] of updateEntries)
						values[key] = value;
					for (const [key, vals] of additionEntries)
						values[key] = mergeAddition(row?.ai?.[key], vals);
					for (const [key, entry] of appendEntries)
						values[key] = appendText(row?.ai?.[key], entry);
				}
				if (Object.keys(values).length)
					valuesById.set(id, values);
			}

			// Aggiornamento ottimistico: riflette subito il cambiamento in tabella/editor.
			valuesById.forEach((values, id) => {
				Object.entries(values).forEach(([key, value]) => setField(id, key, value));
			});

			// Persiste subito ogni card toccata: a differenza dell'editing riga-per-riga,
			// qui "Applica" è già l'azione esplicita di conferma sull'intera selezione — non
			// deve richiedere un "Salva" separato, altrimenti le modifiche si perdono al refresh.
			let savedCount = 0;
			let errorCount = 0;
			for (const [id, values] of valuesById) {
				const patch = {};
				for (const [key, value] of Object.entries(values))
					patch[key.startsWith("banner.") ? key : `ai.${key}`] = value;

				try {
					const { data } = await api.patch(`/api/cards/${id}`, patch);
					const updatedCard = normalizeCard(data);
					setRows((prev) => prev.map((r) => (r._id === id ? (updatedCard || data) : r)));
					setDirty((prev) => {
						if (!prev[id]) return prev;
						const nextEntry = { ...prev[id] };
						Object.keys(patch).forEach((k) => delete nextEntry[k]);
						const next = { ...prev };
						if (Object.keys(nextEntry).length) next[id] = nextEntry;
						else delete next[id];
						return next;
					});
					savedCount++;
				}
				catch (error) {
					errorCount++;
				}
			}

			if (skippedBanners > 0) {
				const bannerText = skippedBanners === 1 ? "1 banner" : `${skippedBanners} banner`;
				const skipMessage = `Su ${bannerText} si applica solo la Categoria: gli altri campi non esistono sui banner e sono stati saltati.`;
				toast.warning?.(skipMessage) || message.warning(skipMessage);
			}

			if (savedCount === 0 && errorCount === 0) {
				if (!skippedBanners)
					toast.warning?.("Nessuna modifica da applicare") || message.warning("Nessuna modifica da applicare");
			}
			else if (errorCount === 0) {
				toast.success?.(`Modifiche salvate su ${savedCount} ${savedCount === 1 ? "prodotto" : "prodotti"}`) ||
					message.success(`Modifiche salvate su ${savedCount} ${savedCount === 1 ? "prodotto" : "prodotti"}`);
			}
			else if (savedCount > 0) {
				toast.warning?.(`Salvate ${savedCount}, ${errorCount} ${errorCount === 1 ? "errore" : "errori"}`) ||
					message.warning(`Salvate ${savedCount}, ${errorCount} ${errorCount === 1 ? "errore" : "errori"}`);
			}
			else {
				toast.error?.("Errore durante il salvataggio delle modifiche") ||
					message.error("Errore durante il salvataggio delle modifiche");
			}
		}
		finally {
			setBulkApplying(false);
			// Modal remains open to allow further edits
		}
	}, [setField, setRows, setDirty, toast]);

	const handleCardMove = React.useCallback(async ({ cardId, targetPage, targetPosition }) => {
		if (!cardId || targetPage == null) return;

		try {
			const { data } = await api.patch(`/api/cards/${cardId}/move`, {
				targetPage,
				targetPosition
			});

			if (data.ok && data.changes?.length > 0) {
				// Aggiorna lo stato locale con le nuove posizioni
				setRows((prev) => {
					const changesMap = new Map(data.changes.map(c => [c.cardId, c]));
					return prev.map((row) => {
						const change = changesMap.get(String(row._id));
						if (change) {
							return {
								...row,
								page: change.page,
								crop: change.crop
							};
						}
						return row;
					});
				});

				toast.success?.("Card spostata con successo") || message.success("Card spostata con successo");
			}
		}
		catch (error) {
			const errMsg = error?.response?.data?.error || "Errore durante lo spostamento";
			toast.error?.(errMsg) || message.error(errMsg);
		}
	}, [api, toast, setRows]);

	const handleBulkDelete = React.useCallback(async (cardIds, onClearSelection) => {
		if (!Array.isArray(cardIds) || !cardIds.length) return;

		// Conferma eliminazione
		const confirmed = await new Promise((resolve) => {
			modal.confirm({
				title: `Eliminare ${cardIds.length} ${cardIds.length === 1 ? "card" : "card"}?`,
				content: "Questa azione non può essere annullata.",
				okText: "Elimina",
				cancelText: "Annulla",
				okButtonProps: { danger: true },
				onOk: () => resolve(true),
				onCancel: () => resolve(false),
			});
		});

		if (!confirmed) return;

		let successCount = 0;
		let errorCount = 0;

		for (const cardId of cardIds) {
			try {
				await api.delete(`/api/cards/${cardId}`);
				successCount++;
			}
			catch {
				errorCount++;
			}
		}

		// Rimuovi le card eliminate dallo stato locale
		if (successCount > 0) {
			const deletedSet = new Set(cardIds);
			setRows((prev) => prev.filter((row) => !deletedSet.has(String(row._id))));

			// Pulisci la selezione nel modal
			onClearSelection?.();

			if (errorCount === 0) {
				toast.success?.(`${successCount} card eliminate`) || message.success(`${successCount} card eliminate`);
			}
			else {
				toast.warning?.(`${successCount} eliminate, ${errorCount} fallite`) || message.warning(`${successCount} eliminate, ${errorCount} fallite`);
			}
		}
		else if (errorCount > 0) {
			toast.error?.("Eliminazione fallita") || message.error("Eliminazione fallita");
		}
	}, [api, toast, modal, setRows]);

	const saveRow = async (row, all = false) => {
		if (!dirty[row._id]) return true;

		setSaving((s) => ({ ...s, [row._id]: true }));

		try {
			const cardId = row._id;

			let patch = { ...dirty[row._id] };
			const uploadsForCard = pendingUploads[cardId];

			if (uploadsForCard) {
				for (const [assetType, pending] of Object.entries(uploadsForCard)) {
					if (!pending || !pending.file) continue;
					const isImage = assetType === "image";
					const label = isImage
						? String((patch["banner.banner"] ?? row?.banner?.banner ?? "")).trim()
						: String((patch["banner.banner_video"] ?? row?.banner?.banner_video ?? "")).trim();
					if (!label) {
						throw new Error(
							isImage
								? "Inserisci il testo del banner prima di salvare l'immagine"
								: "Inserisci il testo del banner video prima di salvare il video",
						);
					}
					const renamed = renameFileWithLabel(pending.file, label);
					const formData = new FormData();
					formData.append("asset", renamed);
					formData.append("label", label);
					let response;
					try {
						response = await api.post(`/api/flyer-assets/${cardId}`, formData, {
							params: { type: assetType },
							headers: { "Content-Type": "multipart/form-data" },
						});
					}
					catch (error) {
						const uploadMessage =
							error?.response?.data?.message ||
							error?.message ||
							(isImage
								? "Impossibile caricare l'immagine"
								: "Impossibile caricare il video");
						throw new Error(uploadMessage);
					}
					const assetPath = response?.data?.assetPath;
					if (!assetPath)
						throw new Error("Risposta server non valida");
					if (isImage) {
						patch["banner.assets.image"] = assetPath;
						if (assetPath.toLowerCase().trim().endsWith(".gif")) {
							patch["banner.assets.gif"] = assetPath;
						}
						else {
							patch["banner.assets.gif"] = "";
						}
					}
					else {
						patch["banner.assets.video"] = assetPath;
					}
				}
			}

			const { data } = await api.patch(`/api/cards/${cardId}`, patch);
			const updatedCard = normalizeCard(data);

			const removalInfo = pendingRemovals[cardId];
			if (removalInfo) {
				const bannerAssets = (updatedCard || data)?.banner?.assets || {};
				const currentImagePaths = new Set();
				const currentImageValue =
					typeof bannerAssets.image === "string" ? bannerAssets.image.trim() : "";
				const currentGifValue =
					typeof bannerAssets.gif === "string" ? bannerAssets.gif.trim() : "";
				const currentVideoValue =
					typeof bannerAssets.video === "string" ? bannerAssets.video.trim() : "";
				if (currentImageValue)
					currentImagePaths.add(currentImageValue);
				if (currentGifValue)
					currentImagePaths.add(currentGifValue);
				const normalizedImageRemoval =
					typeof removalInfo.image === "string" ? removalInfo.image.trim() : "";
				const normalizedVideoRemoval =
					typeof removalInfo.video === "string" ? removalInfo.video.trim() : "";
				const removalRequests = [];
				if (normalizedImageRemoval && !currentImagePaths.has(normalizedImageRemoval)) {
					removalRequests.push({ type: "image", path: normalizedImageRemoval });
				}
				if (normalizedVideoRemoval && normalizedVideoRemoval !== currentVideoValue) {
					removalRequests.push({ type: "video", path: normalizedVideoRemoval });
				}
				const failed = {};
				if (removalRequests.length) {
					const results = await Promise.allSettled(
						removalRequests.map((item) =>
							api.delete(`/api/flyer-assets/${cardId}`, {
								params: { type: item.type, assetPath: item.path },
							})
						)
					);
					results.forEach((result, index) => {
						if (result.status === "rejected") {
							const item = removalRequests[index];
							failed[item.type] = item.path;
							const description =
								result.reason?.response?.data?.message ||
								result.reason?.message ||
								(item.type === "image"
									? "Impossibile rimuovere l'immagine dal server"
									: "Impossibile rimuovere il video dal server");
							toast.error?.(description) || message.error(description);
						}
					});
				}
				if (Object.keys(failed).length) {
					setPendingRemovals((prev) => ({ ...prev, [cardId]: failed }));
				}
				else {
					setPendingRemovals((prev) => {
						if (!prev[cardId]) return prev;
						const next = { ...prev };
						delete next[cardId];
						return next;
					});
				}
			}

			setRows((prev) => prev.map((r) => (r._id === cardId ? (updatedCard || data) : r)));
			setDirty((prev) => {
				const x = { ...prev };
				delete x[cardId];
				return x;
			});

			clearPendingUploadsForCard(cardId);

			if (!all)
				toast.success?.("Riga salvata") || message.success("Riga salvata");

			return true;
		}
		catch (error) {
			clearPendingUploadsForCard(row._id);
			const description = error?.response?.data?.message || error?.message || "Errore salvataggio";
			toast.error?.(description) || message.error(description);
			return false;
		}
		finally {
			setSaving((s) => ({ ...s, [row._id]: false }));
		}

	};

	const toggleValidated = async (row, checked) => {
		setSaving((s) => ({ ...s, [row._id]: true }));
		try {
			// "Valida" salva prima le eventuali modifiche pendenti, poi flagga la card.
			// Se il salvataggio fallisce non procediamo: la card resta da validare e
			// l'errore è già stato notificato da saveRow.
			if (checked && dirty[row._id]) {
				const saved = await saveRow(row, true);
				if (!saved) return false;
				// saveRow azzera il proprio flag di saving nel finally: lo riattiviamo per
				// tenere lo switch in loading anche durante la PATCH di validazione.
				setSaving((s) => ({ ...s, [row._id]: true }));
			}
			const { data } = await api.patch(`/api/cards/${row._id}`, { status: checked ? "validated" : "new" });
			const updatedCard = normalizeCard(data);
			setRows((prev) => prev.map((r) => (r._id === row._id ? (updatedCard || data) : r)));
			return true;
		}
		finally {
			setSaving((s) => ({ ...s, [row._id]: false }));
		}
	};

	// Valida in blocco tutti i prodotti NON ancora validati di UNA pagina (richiesta cliente,
	// simmetrica a handleUnvalidatePage). Riusa toggleValidated card-per-card così il
	// comportamento (salvataggio modifiche pendenti + PATCH status) resta identico allo switch singolo.
	const handleValidatePage = async (pageItems) => {
		const pendingRows = (pageItems || []).filter((it) => it.status !== "validated");
		if (!pendingRows.length) return;

		const confirmed = await new Promise((resolve) => {
			modal.confirm({
				title: `Convalidare ${pendingRows.length} ${pendingRows.length === 1 ? "prodotto" : "prodotti"} di questa pagina?`,
				content: "Le eventuali modifiche non salvate verranno salvate automaticamente. I prodotti convalidati non saranno più modificabili.",
				okText: "Convalida tutti",
				cancelText: "Annulla",
				onOk: () => resolve(true),
				onCancel: () => resolve(false),
			});
		});

		if (!confirmed) return;

		let successCount = 0;
		let errorCount = 0;

		for (const row of pendingRows) {
			try {
				const ok = await toggleValidated(row, true);
				if (ok) successCount++;
				else errorCount++;
			}
			catch {
				errorCount++;
			}
		}

		if (errorCount === 0) {
			toast.success?.(`${successCount} prodotti convalidati`) || message.success(`${successCount} prodotti convalidati`);
		}
		else {
			toast.warning?.(`${successCount} convalidati, ${errorCount} falliti`) || message.warning(`${successCount} convalidati, ${errorCount} falliti`);
		}
	};

	// Rimuove la validazione da tutti i prodotti già validati di UNA pagina (richiesta cliente:
	// tasto per pagina, non la selezione massiva). Riusa toggleValidated card-per-card così il
	// comportamento (PATCH status + aggiornamento riga) resta identico allo switch singolo.
	const handleUnvalidatePage = async (pageItems) => {
		const validatedRows = (pageItems || []).filter((it) => it.status === "validated");
		if (!validatedRows.length) return;

		const confirmed = await new Promise((resolve) => {
			modal.confirm({
				title: `Togliere la validazione da ${validatedRows.length} ${validatedRows.length === 1 ? "prodotto" : "prodotti"} di questa pagina?`,
				content: "Torneranno tutti allo stato \"Da validare\".",
				okText: "Togli validazione",
				cancelText: "Annulla",
				onOk: () => resolve(true),
				onCancel: () => resolve(false),
			});
		});

		if (!confirmed) return;

		let successCount = 0;
		let errorCount = 0;

		for (const row of validatedRows) {
			try {
				await toggleValidated(row, false);
				successCount++;
			}
			catch {
				errorCount++;
			}
		}

		if (errorCount === 0) {
			toast.success?.(`Validazione rimossa da ${successCount} prodotti`) || message.success(`Validazione rimossa da ${successCount} prodotti`);
		}
		else {
			toast.warning?.(`${successCount} aggiornati, ${errorCount} falliti`) || message.warning(`${successCount} aggiornati, ${errorCount} falliti`);
		}
	};

	const handleSaveAll = React.useCallback(async () => {
		const dirtyCardIds = Object.keys(dirty);
		if (!dirtyCardIds.length) {
			toast.info?.("Nessuna modifica da salvare") || message.info("Nessuna modifica da salvare");
			return;
		}

		const rowsToSave = rows.filter((row) => dirtyCardIds.includes(row._id));
		const totalCount = rowsToSave.length;

		if (totalCount === 0) {
			toast.info?.("Nessuna modifica da salvare") || message.info("Nessuna modifica da salvare");
			return;
		}

		let savedCount = 0;
		let errorCount = 0;

		for (const row of rowsToSave) {
			try {
				await saveRow(row, true);
				savedCount++;
			}
			catch (error) {
				errorCount++;
			}
		}

		// Show summary message
		if (savedCount > 0 && errorCount === 0) {
			toast.success?.(
				`Salvate tutte le ${savedCount} ${savedCount === 1 ? "modifica" : "modifiche"}`
			) || message.success(`Salvate tutte le ${savedCount} ${savedCount === 1 ? "modifica" : "modifiche"}`);
		}
		else if (savedCount > 0 && errorCount > 0) {
			toast.warning?.(
				`Salvate ${savedCount} ${savedCount === 1 ? "modifica" : "modifiche"}, ${errorCount} ${errorCount === 1 ? "errore" : "errori"}`
			) ||
				message.warning(
					`Salvate ${savedCount} ${savedCount === 1 ? "modifica" : "modifiche"}, ${errorCount} ${errorCount === 1 ? "errore" : "errori"}`
				);
		}
		else {
			toast.error?.(
				`Errore nel salvataggio: ${errorCount} ${errorCount === 1 ? "errore" : "errori"}`
			) || message.error(`Errore nel salvataggio: ${errorCount} ${errorCount === 1 ? "errore" : "errori"}`);
		}
	}, [dirty, rows, saveRow, toast]);

	const handleResetAll = React.useCallback(() => {
		const dirtyCardIds = Object.keys(dirty);
		if (!dirtyCardIds.length) {
			toast.info?.("Nessuna modifica da annullare") || message.info("Nessuna modifica da annullare");
			return;
		}

		// Clear all dirty state
		setDirty({});

		// Clear all pending uploads
		setPendingUploads({});

		// Clear all pending removals
		setPendingRemovals({});

		// Reload all rows to reset to original state
		if (flyerId) {
			(async () => {
				try {
					await loadCards();
					toast.success?.("Tutte le modifiche sono state annullate") ||
						message.success("Tutte le modifiche sono state annullate");
				}
				catch (error) {
					toast.error?.("Errore nel ricaricamento dei dati") || message.error("Errore nel ricaricamento dei dati");
				}
			})();
		}
	}, [dirty, flyerId, loadCards, toast]);

	const handleDownloadPdf = React.useCallback(async () => {
		if (!flyerId) {
			toast.error?.("ID volantino mancante") || message.error("ID volantino mancante");
			return;
		}

		const hideLoading = toast.loading?.("Download PDF in corso...", 0);
		try {
			triggerBrowserDownload(`/api/flyers/${flyerId}/export.pdf`);
		}
		catch (error) {
			const description = error?.message || "Errore download PDF";
			toast.error?.(description) || message.error(description);
		}
		finally {
			if (typeof hideLoading === "function")
				hideLoading();
		}
	}, [flyerId, toast]);

	const handleExportResources = React.useCallback(async () => {
		if (!flyerId) {
			toast.error?.("ID volantino mancante") || message.error("ID volantino mancante");
			return;
		}

		const hideLoading = toast.loading?.("Esportazione in corso...", 0);
		try {
			triggerBrowserDownload(`/api/flyers/${flyerId}/export`);
			toast.success?.("Esportazione completata!") || message.success("Esportazione completata!");
		}
		catch (error) {
			const description = error?.message || "Errore export risorse";
			toast.error?.(description) || message.error(description);
		}
		finally {
			if (typeof hideLoading === "function")
				hideLoading();
		}
	}, [flyerId, toast]);

	const handleExportXml = React.useCallback(async () => {
		if (!flyerId) return;
		const hideLoading = toast.loading?.("Download XML in corso...", 0);
		try {
			triggerBrowserDownload(`/api/flyers/${flyerId}/export.xml`);
		}
		catch (error) {
			const description = error?.message || "Errore download XML";
			toast.error?.(description) || message.error(description);
		}
		finally {
			if (typeof hideLoading === "function") hideLoading();
		}
	}, [flyerId, toast]);

	const handleExportPages = React.useCallback(async () => {
		if (!flyerId) return;
		const hideLoading = toast.loading?.("Download Pagine in corso...", 0);
		try {
			triggerBrowserDownload(`/api/flyers/${flyerId}/export.pages`);
		}
		catch (error) {
			const description = error?.message || "Errore download Pagine";
			toast.error?.(description) || message.error(description);
		}
		finally {
			if (typeof hideLoading === "function") hideLoading();
		}
	}, [flyerId, toast]);

	const handleExportRefs = React.useCallback(async () => {
		if (!flyerId) return;
		const hideLoading = toast.loading?.("Download Referenze in corso...", 0);
		try {
			triggerBrowserDownload(`/api/flyers/${flyerId}/export.refs`);
		}
		catch (error) {
			const description = error?.message || "Errore download Referenze";
			toast.error?.(description) || message.error(description);
		}
		finally {
			if (typeof hideLoading === "function") hideLoading();
		}
	}, [flyerId, toast]);

	const handleCreateCategory = React.useCallback(async (value) => {
		const name = String(value || "").trim();
		if (!name) return null;
		if (!cooperativaId) {
			toast.warning?.("Seleziona una cooperativa per creare categorie") || message.warning("Seleziona una cooperativa per creare categorie");
			return null;
		}
		try {
			const { data } = await api.post("/api/categories", { name, cooperativaId });
			const item = data?.category || data;
			if (!item) return null;
			const normalized = {
				value: String(item.id || item._id),
				name: item.name,
				imagePath: item.imagePath || null,
				imageUrl: resolveCategoryImage(item.imagePath),
				description: item.description || ""
			};
			setCategoryOptions((prev) => {
				if (prev.some((opt) => String(opt.value) === normalized.value)) return prev;
				return [...prev, normalized].sort((a, b) => a.name.localeCompare(b.name));
			});
			toast.success?.("Categoria creata") || message.success("Categoria creata");
			return normalized;
		}
		catch (error) {
			const errMsg = error?.response?.data?.error || "Impossibile creare la categoria";
			toast.error?.(errMsg) || message.error(errMsg);
			return null;
		}
	}, [api, cooperativaId, resolveCategoryImage, toast]);

	const handleCreateClass = React.useCallback(async (value) => {
		const name = String(value || "").trim();
		if (!name) return null;
		if (!cooperativaId) {
			toast.warning?.("Seleziona una cooperativa per creare classi") || message.warning("Seleziona una cooperativa per creare classi");
			return null;
		}
		try {
			const { data } = await api.post("/api/classes", { name, cooperativaId });
			const item = data?.classe || data;
			if (!item) return null;
			const normalized = {
				value: String(item.id || item._id),
				name: item.name,
				description: item.description || ""
			};
			setClassOptions((prev) => {
				if (prev.some((opt) => String(opt.value) === normalized.value)) return prev;
				return [...prev, normalized].sort((a, b) => a.name.localeCompare(b.name));
			});
			toast.success?.("Classe creata") || message.success("Classe creata");
			return normalized;
		}
		catch (error) {
			const errMsg = error?.response?.data?.error || "Impossibile creare la classe";
			toast.error?.(errMsg) || message.error(errMsg);
			return null;
		}
	}, [api, cooperativaId, toast, message]);

	const handleCreateFund = React.useCallback(async (value) => {
		const name = String(value || "").trim();
		if (!name) return null;
		if (!cooperativaId) {
			toast.warning?.("Seleziona una cooperativa per creare fondi") || message.warning("Seleziona una cooperativa per creare fondi");
			return null;
		}
		try {
			const { data } = await api.post("/api/funds", { name, cooperativaId });
			const item = data?.fund || data;
			if (!item) return null;
			const normalized = {
				value: String(item.id || item._id),
				name: item.name,
				description: item.description || ""
			};
			setFundOptions((prev) => {
				if (prev.some((opt) => String(opt.value) === normalized.value)) return prev;
				return [...prev, normalized].sort((a, b) => a.name.localeCompare(b.name));
			});
			toast.success?.("Fondo creato") || message.success("Fondo creato");
			return normalized;
		}
		catch (error) {
			const errMsg = error?.response?.data?.error || "Impossibile creare il fondo";
			toast.error?.(errMsg) || message.error(errMsg);
			return null;
		}
	}, [api, cooperativaId, toast, message]);

	const handleCreateBadge = React.useCallback(async (value) => {
		const name = String(value || "").trim();
		if (!name) return null;
		if (!cooperativaId) {
			toast.warning?.("Seleziona una cooperativa per creare bollini") || message.warning("Seleziona una cooperativa per creare bollini");
			return null;
		}
		try {
			const { data } = await api.post("/api/badges", { name, cooperativaId });
			const item = data?.badge || data;
			if (!item) return null;
			const normalized = {
				value: String(item.id || item._id),
				name: item.name,
				imagePath: item.imagePath || null,
				imageUrl: resolveCategoryImage(item.imagePath),
				description: item.description || ""
			};
			setBadgeOptions((prev) => {
				if (prev.some((opt) => String(opt.value) === normalized.value)) return prev;
				return [...prev, normalized].sort((a, b) => a.name.localeCompare(b.name));
			});
			toast.success?.("Bollino creato") || message.success("Bollino creato");
			return normalized;
		}
		catch (error) {
			const errMsg = error?.response?.data?.error || "Impossibile creare il bollino";
			toast.error?.(errMsg) || message.error(errMsg);
			return null;
		}
	}, [api, cooperativaId, resolveCategoryImage, toast, message]);

	const handleCreateFilter = React.useCallback(async (value) => {
		const name = String(value || "").trim();
		if (!name) return null;
		if (!cooperativaId) {
			toast.warning?.("Seleziona una cooperativa per creare filtri") || message.warning("Seleziona una cooperativa per creare filtri");
			return null;
		}
		try {
			const { data } = await api.post("/api/filters", { name, cooperativaId });
			const item = data?.filtro || data;
			if (!item) return null;
			const normalized = {
				value: String(item.id || item._id),
				name: item.name,
				description: item.description || ""
			};
			setFilterOptions((prev) => {
				if (prev.some((opt) => String(opt.value) === normalized.value)) return prev;
				return [...prev, normalized].sort((a, b) => a.name.localeCompare(b.name));
			});
			toast.success?.("Filtro creato") || message.success("Filtro creato");
			return normalized;
		}
		catch (error) {
			const errMsg = error?.response?.data?.error || "Impossibile creare il filtro";
			toast.error?.(errMsg) || message.error(errMsg);
			return null;
		}
	}, [api, cooperativaId, toast, message]);

	const handleCreateMechanic = React.useCallback(async (value) => {
		const name = String(value || "").trim();
		if (!name) return null;
		if (!cooperativaId) {
			toast.warning?.("Seleziona una cooperativa per creare meccaniche") || message.warning("Seleziona una cooperativa per creare meccaniche");
			return null;
		}
		try {
			const { data } = await api.post("/api/mechanics", { name, cooperativaId });
			const item = data?.meccanica || data;
			if (!item) return null;
			const normalized = {
				value: String(item.id || item._id),
				name: item.name,
				description: item.description || ""
			};
			setMechanicOptions((prev) => {
				if (prev.some((opt) => String(opt.value) === normalized.value)) return prev;
				return [...prev, normalized].sort((a, b) => a.name.localeCompare(b.name));
			});
			toast.success?.("Meccanica creata") || message.success("Meccanica creata");
			return normalized;
		}
		catch (error) {
			const errMsg = error?.response?.data?.error || "Impossibile creare la meccanica";
			toast.error?.(errMsg) || message.error(errMsg);
			return null;
		}
	}, [api, cooperativaId, toast, message]);

	const resetRow = React.useCallback(async (row) => {
		if (!row?._id || !flyerId) return;
		try {
			const list = await loadCards();
			const fresh = list.find((item) => item._id === row._id);
			if (fresh) {
				const normalizedFresh = normalizeCard(fresh);
				setRows((prev) => prev.map((item) => (item._id === row._id ? (normalizedFresh || fresh) : item)));
			}
			setDirty((prev) => {
				if (!prev[row._id]) return prev;
				const next = { ...prev };
				delete next[row._id];
				return next;
			});
			setPendingRemovals((prev) => {
				if (!prev[row._id]) return prev;
				const next = { ...prev };
				delete next[row._id];
				return next;
			});

			clearPendingUploadsForCard(row._id);
		}
		catch (error) {
			toast.error?.("Impossibile ripristinare la riga") || message.error("Impossibile ripristinare la riga");
		}
	}, [flyerId, loadCards, setRows, setDirty, setPendingRemovals, clearPendingUploadsForCard, toast]);

	const deleteRow = React.useCallback(async (row) => {
		if (!row?._id) return;
		try {
			await api.delete(`/api/cards/${row._id}`);
			toast.success?.("Card eliminata") || message.success("Card eliminata");
		}
		catch (error) {
			toast.error?.(error?.response?.data?.error || "Errore eliminazione") || message.error(error?.response?.data?.error || "Errore eliminazione");
		}
	}, [toast, message]);


	// Filtraggio per ricerca + stato dentro la tab corrente
	const filterItems = React.useCallback(
		(items) => {
			const q = query.trim().toLowerCase();
			return items.filter((it) => {
				const okStatus =
					statusFilter === "all" ||
					(statusFilter === "todo" && it.status !== "validated") ||
					(statusFilter === "ok" && it.status === "validated");
				if (!okStatus) return false;
				if (!q) return true;
				const hay = [it.ai?.nome_prodotto, it.ai?.marca, it.ai?.descrizione, it.ai?.note].filter(Boolean).join(" ").toLowerCase();
				return hay.includes(q);
			});
		},
		[query, statusFilter]
	);

	// "Apri card" dal resoconto dei codici clienti: arriva con focusCardId nello stato di
	// navigazione. Porta la card in vista (pagina giusta, filtri che la nascondono azzerati),
	// la evidenzia per un attimo e toglie focusCardId dallo stato per non ripetersi.
	const focusCardId = location.state?.focusCardId;
	React.useEffect(() => {
		if (!focusCardId || !flyerId)
			return;
		const group = pages.find(({ items }) => items.some((it) => String(it._id || it.id) === String(focusCardId)));
		if (!group)
			return;
		const row = group.items.find((it) => String(it._id || it.id) === String(focusCardId));

		if (viewMode !== "pages") {
			setViewMode("pages");
			return;
		}
		if (!filterItems([row]).length) {
			setQuery("");
			setStatusFilter("all");
			return;
		}
		if (String(group.page) !== String(activePage)) {
			navigate(`/flyermanage/${flyerId}/${group.page}`, { replace: true, state: location.state });
			return;
		}

		let cancelled = false;
		let attempts = 0;
		const timers = [];
		const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
		const focus = () => {
			if (cancelled)
				return;
			const el = document.querySelector(`[data-card-id="${CSS.escape(String(focusCardId))}"]`);
			if (!el) {
				if (attempts++ < 30)
					timers.push(setTimeout(focus, 100));
				return;
			}
			el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
			el.classList.add("fm-card--focus");
			timers.push(setTimeout(() => el.classList.remove("fm-card--focus"), 2600));
			const { focusCardId: _done, ...rest } = location.state || {};
			navigate(location.pathname, { replace: true, state: rest });
		};
		focus();
		return () => {
			cancelled = true;
			timers.forEach(clearTimeout);
		};
	}, [focusCardId, flyerId, pages, viewMode, filterItems, activePage, navigate, location.pathname, location.state]);

	const filterBySelection = React.useCallback((list) => {
		if (viewMode === "categories") {
			if (!selectedCategory || selectedCategory === "tutte") return list;
			if (selectedCategory === "nessuna")
				return list.filter((r) => !r?.ai?.categoria);
			const key = String(selectedCategory);
			const keyLower = key.toLowerCase();
			return list.filter((r) => {
				const value = r?.ai?.categoria;
				if (!value) return false;
				const current = String(value);
				return current === key || current.toLowerCase() === keyLower;
			});
		}
		if (viewMode === "classes") {
			if (!selectedClass || selectedClass === "tutte") return list;
			if (selectedClass === "nessuna")
				return list.filter((r) => !r?.ai?.classe);
			const key = String(selectedClass);
			const keyLower = key.toLowerCase();
			return list.filter((r) => {
				const value = r?.ai?.classe;
				if (!value) return false;
				const current = String(value);
				return current === key || current.toLowerCase() === keyLower;
			});
		}
		if (viewMode === "funds") {
			if (!selectedFund || selectedFund === "tutte") return list;
			if (selectedFund === "nessuna")
				return list.filter((r) => !r?.ai?.fondi);
			const key = String(selectedFund);
			const keyLower = key.toLowerCase();
			return list.filter((r) => {
				const value = r?.ai?.fondi;
				if (!value) return false;
				const current = String(value);
				return current === key || current.toLowerCase() === keyLower;
			});
		}
		if (viewMode === "badges") {
			if (!selectedBadge || selectedBadge === "tutti") return list;
			if (selectedBadge === "nessuna")
				return list.filter((r) => normalizeIdArray(r?.ai?.bollino).length === 0);
			const key = String(selectedBadge);
			const keyLower = key.toLowerCase();
			return list.filter((r) => {
				const values = normalizeIdArray(r?.ai?.bollino);
				if (!values.length) return false;
				return values.some((value) => {
					const current = String(value);
					return current === key || current.toLowerCase() === keyLower;
				});
			});
		}
		if (viewMode === "filters") {
			if (!selectedFilter || selectedFilter === "tutti") return list;
			if (selectedFilter === "nessuna")
				return list.filter((r) => normalizeIdArray(r?.ai?.filtro).length === 0);
			const key = String(selectedFilter);
			const keyLower = key.toLowerCase();
			return list.filter((r) => {
				const values = normalizeIdArray(r?.ai?.filtro);
				if (!values.length) return false;
				return values.some((value) => {
					const current = String(value);
					return current === key || current.toLowerCase() === keyLower;
				});
			});
		}
		if (viewMode === "mechanics") {
			if (!selectedMechanic || selectedMechanic === "tutte") return list;
			if (selectedMechanic === "nessuna")
				return list.filter((r) => normalizeIdArray(r?.ai?.meccanica).length === 0);
			const key = String(selectedMechanic);
			const keyLower = key.toLowerCase();
			return list.filter((r) => {
				const values = normalizeIdArray(r?.ai?.meccanica);
				if (!values.length) return false;
				return values.some((value) => {
					const current = String(value);
					return current === key || current.toLowerCase() === keyLower;
				});
			});
		}
		return list;
	}, [viewMode, selectedCategory, selectedClass, selectedBadge, selectedFilter, selectedFund, selectedMechanic]);

	const filteredRows = React.useMemo(
		() => filterBySelection(filterItems(rows)),
		[rows, filterItems, filterBySelection]
	);
	const deferredFilteredRows = React.useDeferredValue(filteredRows);
	const filteredRowCount = Array.isArray(filteredRows) ? filteredRows.length : 0;
	const shouldEnableProgressive = React.useMemo(() => {
		if (viewMode === "pages") return false;
		const MIN_PROGRESSIVE_SIZE = 120;
		return filteredRowCount > MIN_PROGRESSIVE_SIZE;
	}, [viewMode, filteredRowCount]);
	const { items: progressiveRows, isPending: isProgressivePending } = useProgressiveList(
		deferredFilteredRows,
		{
			enabled: shouldEnableProgressive,
			initialBatch: 96,
			batchSize: 96,
			idleTimeout: 20,
			threshold: 120
		}
	);
	const isDeferredFiltering = deferredFilteredRows !== filteredRows;
	const progressiveCardEditors = React.useMemo(
		() =>
			progressiveRows.map((row) => (
				<FlyerCardEditor
					key={row._id}
					row={row}
					categoryOptions={categorySelectOptions}
					categoryLoading={categoryLoading}
					classOptions={classSelectOptions}
					classLoading={classLoading}
					fundOptions={fundSelectOptions}
					fundLoading={fundLoading}
					badgeOptions={badgeSelectOptions}
					badgeLoading={badgeLoading}
					filterOptions={filterSelectOptions}
					filterLoading={filterLoading}
					mechanicOptions={mechanicSelectOptions}
					mechanicLoading={mechanicLoading}
					productFields={productFields}
					productFieldsLoading={combinedProductFieldsLoading}
					genericTaxonomies={genericTaxonomies}
					availablePages={pageNumbers}
					saving={saving}
					dirty={dirty}
					onFieldChange={setField}
					onSave={saveRow}
					onReset={resetRow}
					onDelete={deleteRow}
					onReprocess={handleReprocessCard}
					isReprocessing={row?.seriale != null && processingSerials.has(row.seriale)}
					reprocessDisabled={aiProcessing}
					onToggleValidated={toggleValidated}
					onCreateCategory={handleCreateCategory}
					onCreateClass={handleCreateClass}
					onCreateFund={handleCreateFund}
					onCreateBadge={handleCreateBadge}
					onCreateFilter={handleCreateFilter}
					onCreateMechanic={handleCreateMechanic}
					getImageUrl={imgUrl}
					onServerCardUpdate={handleServerCardUpdate}
					pendingBannerAssets={pendingUploads[row._id] || {}}
					onPendingAssetChange={handlePendingAssetChange}
					loading={loading}
				/>
			)),
		[
			progressiveRows,
			categorySelectOptions,
			categoryLoading,
			classSelectOptions,
			classLoading,
			badgeSelectOptions,
			badgeLoading,
			filterSelectOptions,
			filterLoading,
			mechanicSelectOptions,
			mechanicLoading,
			fundSelectOptions,
			fundLoading,
			pageNumbers,
			saving,
			dirty,
			setField,
			saveRow,
			resetRow,
			deleteRow,
			toggleValidated,
			handleCreateCategory,
			handleCreateClass,
			handleCreateFund,
			handleCreateBadge,
			handleCreateFilter,
			handleCreateMechanic,
			imgUrl,
			loading,
			pendingUploads,
			handlePendingAssetChange,
			handleServerCardUpdate,
			productFields,
			combinedProductFieldsLoading,
			genericTaxonomies
		]
	);
	const progressiveLoaderText = isDeferredFiltering ? "Aggiornamento dei risultati…" : "Caricamento altre card…";
	const showProgressiveLoader = isDeferredFiltering || isProgressivePending;

	// Colore configurato in Impostazioni > Descrizioni AI per ciascun campo tassonomia
	// (fallback alla palette storica se il campo non è ancora stato personalizzato).
	const categoryColor = fieldRegistryMap.get("categoria")?.color || "green";
	const classColor = fieldRegistryMap.get("classe")?.color || "blue";
	const fundColor = fieldRegistryMap.get("fondi")?.color;
	const badgeColor = fieldRegistryMap.get("bollino")?.color || "orange";
	const filterColor = fieldRegistryMap.get("filtro")?.color || "blue";
	const mechanicColor = fieldRegistryMap.get("meccanica")?.color || "green";

	// Ricrea la stessa "ricetta" delle classi CSS fm-pill-*/fm-badge.* (sfondo chiaro +
	// bordo + testo) per un colore qualsiasi (preset AntD o hex), non solo la palette fissa.
	//
	// Il colore passa SEMPRE da toHexColor, come già fanno i Tag dentro la card. I preset
	// arrivano dal registry come parole chiave ("blue", "geekblue", "volcano"…): alcune sono
	// anche colori CSS validi ma di tinta diversa da quella AntD (blue = #0000FF invece di
	// #1677ff), altre — geekblue, volcano, lime — non esistono affatto come keyword CSS, e lì
	// color-mix scartava l'intera dichiarazione facendo ricadere il campo sul colore della
	// classe fm-pill-* di turno: è il motivo per cui Meccaniche appariva dello stesso verde di
	// Categorie.
	const taxonomyChipStyle = React.useCallback((color) =>
	{
		if (!color)
			return undefined;
		const hex = toHexColor(color);
		return {
			background: `color-mix(in srgb, ${hex} 12%, white)`,
			borderColor: `color-mix(in srgb, ${hex} 45%, white)`,
			color: hex
		};
	}, []);

	const categoryTabs = React.useMemo(
		() => categories.map((cat) => ({
			key: String(cat.id),
			label: (
				<Space size={6}>
					<span style={{color: categoryColor}}>{cat.name}</span>
					<Tag color={categoryColor} className="fm-badge">{cat.count}</Tag>
				</Space>
			)
		})),
		[categories, categoryColor]
	);

	const classTabs = React.useMemo(
		() => classFilters.map((cls) => {
			const initials = String(cls.name || cls.id).trim().slice(0, 2).toUpperCase() || "?";
			return {
				key: String(cls.id),
				label: (
					<Space size={6}>
						<span style={{color: classColor}}>{cls.name}</span>
						<Tag color={classColor} className="fm-badge">{cls.count}</Tag>
					</Space>
				)
			};
		}),
		[classFilters, classColor]
	);

	const fundTabs = React.useMemo(
		() => fundFilters.map((flt) => {
			return {
				key: String(flt.id),
				label: (
					<Space size={6}>
						<span className="fm-badge" style={fundColor ? {color: fundColor} : undefined}>{flt.name}</span>
						<Tag color={fundColor} className="fm-badge">{flt.count}</Tag>
					</Space>
				)
			};
		}),
		[fundFilters, fundColor]
	);

	const badgeTabs = React.useMemo(
		() => badgeFilters.map((badge) => ({
			key: String(badge.id),
			label: (
				<Space size={6}>
					<span style={{color: badgeColor}}>{badge.name}</span>
					<Tag color={badgeColor} className="fm-badge">{badge.count}</Tag>
				</Space>
			)
		})),
		[badgeFilters, badgeColor]
	);

	const filterTabs = React.useMemo(
		() => filterFilters.map((filterItem) => ({
			key: String(filterItem.id),
			label: (
				<Space size={6}>
					<span style={{color: filterColor}}>{filterItem.name}</span>
					<Tag color={filterColor} className="fm-badge">{filterItem.count}</Tag>
				</Space>
			)
		})),
		[filterFilters, filterColor]
	);

	const mechanicTabs = React.useMemo(
		() => mechanicFilters.map((mechanic) => ({
			key: String(mechanic.id),
			label: (
				<Space size={6}>
					<span style={{color: mechanicColor}}>{mechanic.name}</span>
					<Tag color={mechanicColor} className="fm-badge">{mechanic.count}</Tag>
				</Space>
			)
		})),
		[mechanicFilters, mechanicColor]
	);

	return (
		<>
			<div className="fm-wrap">
				<div className="fm-toolbar">
					<Flex justify="space-between" align="center" style={{ minWidth: 0 }}>
						<Flex gap={10} align="center" style={{ minWidth: 0, flex: 1, marginRight: 16 }}>
							<Button
								icon={<ArrowLeftOutlined />}
								onClick={() => {
									// Torna alla lista volantini della promo di appartenenza;
									// fallback a /flyers se il volantino non ha una promo.
									const pid = flyerData?.promoId;
									const promoId = pid && typeof pid === "object" ? pid._id : pid;
									navigate(promoId ? `/promos/${promoId}` : "/flyers");
								}}
								style={{ flexShrink: 0 }}
							>
								Torna ai Volantini
							</Button>
							<div style={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
								<Title level={3} style={{ margin: 0, display: "flex", alignItems: "center", minWidth: 0 }}>
									{/*<span style={{ flexShrink: 0 }}>Gestisci ·&nbsp;</span>*/}
									<Tooltip title={flyerName}>
										<b style={{
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
											minWidth: 0,
											display: "inline-block",
											flex: 1
										}}>
											{flyerName}
										</b>
									</Tooltip>
								</Title>
								{sellOutLabel && (
									<Text type="secondary" style={{ fontSize: 12, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
										Sell-Out: {sellOutLabel}
									</Text>
								)}
							</div>
						</Flex>
						<Space size={10} align="center">
							<Button type="primary" icon={<BranchesOutlined />} onClick={() => setBulkModalOpen(true)}>
								Selezione massiva
							</Button>
							<Input.Search
								allowClear
								placeholder="Cerca prodotto, marca, note…"
								onSearch={setQuery}
								onChange={(e) => setQuery(e.target.value)}
								style={{ width: 320 }}
							/>
							<Segmented
								value={statusFilter}
								onChange={setStatusFilter}
								options={[
									{ label: "Tutto", value: "all" },
									{
										label: (
											<Space size={6}>
												<HourglassTwoTone twoToneColor={["#E6A700", "#E6A700"]} /> Da validare
											</Space>
										),
										value: "todo"
									},
									{
										label: (
											<Space size={6}>
												<CheckCircleTwoTone twoToneColor={["#2ecc71", "#2ecc71"]} /> Validate
											</Space>
										),
										value: "ok"
									}
								]}
							/>
							<Space size={6} align="center">
								<Text type="secondary">Mostra incomplete</Text>
								<Switch
									checked={showIncompleteCards}
									onChange={setShowIncompleteCards}
								/>
							</Space>
							<Badge color="blue" text={`${rows.length} card`} />
						</Space>
					</Flex>
					<Flex gap={10} justify="flex-end" style={{ marginTop: 10 }}>
						<Button
							icon={<EditOutlined />}
							onClick={() => navigate(flyerData?.isVariant && flyerData?.parentFlyerId ? `/variant-preprocess/${flyerId}` : `/flyerpreprocess/${flyerId}`)}
							disabled={!(flyerData?.pageWidth > 0 || aiProcessing)}
							loading={aiProcessing && ocrProgress > 0 && ocrProgress < 100}
							style={aiProcessing && ocrProgress > 0 && ocrProgress < 100 ? {
								background: "linear-gradient(90deg, #ff9800 " + ocrProgress + "%, #f0f0f0 " + ocrProgress + "%)",
								borderColor: "#ff9800"
							} : {}}
						>
							{aiProcessing && ocrProgress > 0 && ocrProgress < 100
								? `Pre-el. in corso (${ocrProgress}%)`
								: "Apri Pre-elaborazione"}
						</Button>
						<Tooltip title={flyerData?.pageWidth > 0 ? 'Ricarica/Cambia PDF' : 'Pre-elabora PDF'}>
							<Button
								icon={<CloudUploadOutlined />}
								onClick={() => setPdfModalOpen(true)}
							>
								{/*{flyerData?.pageWidth > 0 ? 'Ricarica/Cambia PDF' : 'Pre-elabora PDF'}*/}
							</Button>
						</Tooltip>
						<Button
							icon={<FileExcelOutlined />}
							onClick={() => setExcelModalOpen(true)}
							disabled={aiProcessing}
						>
							Carica Codici Clienti
						</Button>
						{canUnlockProcessing && (
							<Button
								danger
								icon={<DisconnectOutlined />}
								onClick={handleUnlockProcessing}
								loading={unlockingProcessing}
							>
								{flyerData?.status === "ERROR" ? "Sblocca errore" : "Sblocca elaborazione"}
							</Button>
						)}
						<Tooltip title={hasPendingChanges ? "Salva prima tutte le modifiche per esportare le risorse." : ""}>
							<span>
							<Dropdown
								trigger={["click"]}
								menu={{
									items: [
										{ key: "export-pdf", label: "Volantino (PDF)", icon: <DownloadOutlined /> },
										{ key: "export-xml", label: "Solo XML", icon: <DownloadOutlined /> },
										{ key: "export-pages", label: "Solo Pagine (ZIP)", icon: <DownloadOutlined /> },
										{ key: "export-refs", label: "Solo Referenze (ZIP)", icon: <DownloadOutlined /> },
										{ type: "divider" },
										{ key: "export-all", label: "Esporta tutto (ZIP)", icon: <DownloadOutlined />, disabled: hasPendingChanges },
									],
									onClick: ({ key }) => {
										if (key === "export-pdf") handleDownloadPdf();
										else if (key === "export-xml") handleExportXml();
										else if (key === "export-pages") handleExportPages();
										else if (key === "export-refs") handleExportRefs();
										else if (key === "export-all") handleExportResources();
									},
								}}
							>
								<Button icon={<DownloadOutlined />}>
									Esporta Risorse
								</Button>
							</Dropdown>
							</span>
						</Tooltip>
						<Button
							type="primary"
							icon={<SaveOutlined />}
							onClick={handleSaveAll}
							disabled={!hasPendingChanges}
							style={{
								background: hasPendingChanges ? "#52c41a" : undefined,
								borderColor: hasPendingChanges ? "#52c41a" : undefined,
							}}
						>
							Salva tutto {hasPendingChanges && `(${pendingChangesCount})`}
						</Button>
						<Button
							icon={<ReloadOutlined />}
							onClick={handleResetAll}
							disabled={!hasPendingChanges}
							danger
						>
							Annulla tutto
						</Button>
						<Tooltip title="Referenze">
							<Badge count={flyerData?.refsCount || 0} size="small" offset={[-2, 2]} style={{ zIndex: 1 }}>
								<Button icon={<FileImageOutlined />} style={{ minWidth: 32 }} onClick={() => setRefsModalOpen(true)} />
							</Badge>
						</Tooltip>
						<Tooltip title="Modifica informazioni" placement="left">
							<Button
								icon={<FormOutlined />}
								onClick={() => setEditModalOpen(true)}
								disabled={!flyerData}
								style={{
									backgroundColor: "#faad14",
									borderColor: "#faad14",
									color: "#fff",
									minWidth: 32
								}}
							/>
						</Tooltip>
					</Flex>
				</div>

				{
					(pages?.length ?? 0 > 0) ?
						(<div className="fm-subnav">
							<Segmented
								className="fm-subnav-seg"
								value={viewMode}
								onChange={setViewMode}
								options={[
									{
										value: "pages",
										label: (
											<span className="fm-pill fm-pill-red">
												Pagine
												<span className="fm-badge red">{pages?.length ?? 0}</span>
											</span>
										),
									},
									{
										value: "categories",
										label: (
											<span className="fm-pill" style={taxonomyChipStyle(categoryColor)}>
												Categorie
												<span className="fm-badge" style={taxonomyChipStyle(categoryColor)}>{categories?.length ?? 0}</span>
											</span>
										),
									},
									{
										value: "classes",
										label: (
											<span className="fm-pill" style={taxonomyChipStyle(classColor)}>
												Classi
												<span className="fm-badge" style={taxonomyChipStyle(classColor)}>{classFilters?.length ?? 0}</span>
											</span>
										),
									},
									{
										value: "funds",
										label: (
											<span className="fm-pill" style={taxonomyChipStyle(fundColor)}>
												Fondi
												<span className="fm-badge" style={taxonomyChipStyle(fundColor)}>{fundFilters?.length ?? 0}</span>
											</span>
										),
									},
									{
										value: "badges",
										label: (
											<span className="fm-pill" style={taxonomyChipStyle(badgeColor)}>
												Bollini
												<span className="fm-badge" style={taxonomyChipStyle(badgeColor)}>{badgeFilters?.length ?? 0}</span>
											</span>
										),
									},
									{
										value: "filters",
										label: (
											<span className="fm-pill" style={taxonomyChipStyle(filterColor)}>
												Filtri
												<span className="fm-badge" style={taxonomyChipStyle(filterColor)}>{filterFilters?.length ?? 0}</span>
											</span>
										),
									},
									{
										value: "mechanics",
										label: (
											<span className="fm-pill" style={taxonomyChipStyle(mechanicColor)}>
												Meccaniche
												<span className="fm-badge" style={taxonomyChipStyle(mechanicColor)}>{mechanicFilters?.length ?? 0}</span>
											</span>
										),
									},
								].filter((option) => isTaxonomyViewVisible(option.value))}
							/>
						</div>) : ""
				}


				<div className="fm-tabs-wrap">
					{/* EMPTY STATE - Nessuna card */}
					{!loading && pages.length === 0 && (
						<div style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							padding: "80px 40px",
							textAlign: "center",
							background: "linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)",
							margin: "20px 0"
						}}>
							<div style={{
								width: 120,
								height: 120,
								background: "linear-gradient(320deg, #1a1a1a 0%, #d32f2f 60%)",
								borderRadius: "50%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								marginBottom: 24,
								boxShadow: "0 10px 40px rgba(211, 47, 47, 0.3)"
							}}>
								<FileImageOutlined style={{ fontSize: 48, color: "#fff" }} />
							</div>
							<Typography.Title level={3} style={{ marginBottom: 8, color: "#333" }}>
								Nessuna card presente
							</Typography.Title>
							<Typography.Text type="secondary" style={{ fontSize: 16, marginBottom: 24, maxWidth: 400 }}>
								Apri la pre-elaborazione per tracciare le gabbie e avviare l'elaborazione AI.
							</Typography.Text>
							<Button
								type="primary"
								size="large"
								icon={<EditOutlined />}
								disabled={!(flyerData?.pageWidth > 0 || aiProcessing)}
								onClick={() => navigate(flyerData?.isVariant && flyerData?.parentFlyerId ? `/variant-preprocess/${flyerId}` : `/flyerpreprocess/${flyerId}`)}
								style={{
									background: !(flyerData?.pageWidth > 0 || aiProcessing) ? "" : "linear-gradient(320deg, #1a1a1a 0%, #d32f2f 60%)",
									border: "none",
									height: 48,
									paddingInline: 32,
									borderRadius: 24,
									fontWeight: 600,
									boxShadow: "0 4px 15px rgba(211, 47, 47, 0.4)",
								}}
							>
								{aiProcessing && ocrProgress > 0 && ocrProgress < 100
									? `Pre-el. in corso (${ocrProgress}%)`
									: "Apri Pre-elaborazione"}
							</Button>
						</div>
					)}

					{/* VISTA PAGINE */}
					{viewMode === "pages" && pages.length > 0 && (
						<Tabs
							size="small"
							animated
							activeKey={String(activePage ?? "0")}
							onChange={(k) => {
								if (!flyerId) return;
								navigate(`/flyermanage/${flyerId}/${k}`, { state: { flyerName } });
							}}
							items={pages.map(({ page, items }) => {
								const allValidated = items.length > 0 && items.every((it) => it.status === "validated");
								// Tutte le card della pagina (non filtrate da ricerca/segmented control): "tutti i
								// prodotti" richiesto dalla cliente deve valere anche con un filtro attivo.
								const hasValidated = items.some((it) => it.status === "validated");
								return {
								key: String(page),
								label: (
									<Space size={6}>
										{allValidated
											? <CheckCircleTwoTone twoToneColor={["#2ecc71", "#2ecc71"]} />
											: <HourglassTwoTone twoToneColor={["#e74c3c", "#e74c3c"]} />
										}
										<span>Pagina {page}</span>
									</Space>
								),
								children: (
									<div className="fm-page">
										<Space style={{ marginBottom: 12 }}>
											<Button
												size="small"
												icon={<CheckCircleTwoTone twoToneColor={["#2ecc71", "#2ecc71"]} />}
												disabled={allValidated}
												onClick={() => handleValidatePage(items)}
											>
												Convalida pagina
											</Button>
											<Button
												size="small"
												icon={<HourglassTwoTone twoToneColor={["#E6A700", "#E6A700"]} />}
												disabled={!hasValidated}
												onClick={() => handleUnvalidatePage(items)}
											>
												Rimuovi validazione da tutti
											</Button>
										</Space>
										<div className="fm-grid">
											{filterItems(items).map((row) => (
												<FlyerCardEditor
													key={row._id}
													row={row}
													categoryOptions={categorySelectOptions}
													categoryLoading={categoryLoading}
													classOptions={classSelectOptions}
													classLoading={classLoading}
													fundOptions={fundSelectOptions}
													fundLoading={fundLoading}
													badgeOptions={badgeSelectOptions}
													badgeLoading={badgeLoading}
													filterOptions={filterSelectOptions}
													filterLoading={filterLoading}
													mechanicOptions={mechanicSelectOptions}
													mechanicLoading={mechanicLoading}
													productFields={productFields}
													productFieldsLoading={combinedProductFieldsLoading}
													genericTaxonomies={genericTaxonomies}
													availablePages={pageNumbers}
													saving={saving}
													dirty={dirty}
													onFieldChange={setField}
													onSave={saveRow}
													onReset={resetRow}
													onDelete={deleteRow}
													onReprocess={handleReprocessCard}
													isReprocessing={row?.seriale != null && processingSerials.has(row.seriale)}
													reprocessDisabled={aiProcessing}
													onToggleValidated={toggleValidated}
													onCreateCategory={handleCreateCategory}
													onCreateClass={handleCreateClass}
													onCreateFund={handleCreateFund}
													onCreateBadge={handleCreateBadge}
													onCreateFilter={handleCreateFilter}
													onCreateMechanic={handleCreateMechanic}
													getImageUrl={imgUrl}
													onServerCardUpdate={handleServerCardUpdate}
													pendingBannerAssets={pendingUploads[row._id] || {}}
													onPendingAssetChange={handlePendingAssetChange}
													loading={loading}
												/>
											))}
										</div>
									</div>
								),
							}; })}
						/>
					)}

					{/* VISTA CATEGORIE */}
					{viewMode === "categories" && (
						<>
							<Tabs id="fm-categories-tabs"
								size="small"
								animated
								items={categoryTabs}
								activeKey={String(selectedCategory)}
								onChange={(key) => setSelectedCategory(String(key))}
							/>
							{categoryLoading && (
								<div className="fm-categories-placeholder">Caricamento categorie…</div>
							)}
							{!categoryLoading && categories.length <= 1 && (
								<div className="fm-categories-placeholder">Nessuna categoria configurata per questo volantino.</div>
							)}

							{/* griglia card filtrata per categoria */}
							<div className="fm-page">
								<div className="fm-grid">{progressiveCardEditors}</div>
								{showProgressiveLoader && (
									<div className="fm-progressive-loader">
										<Spin size="small" />
										<span>{progressiveLoaderText}</span>
									</div>
								)}
							</div>
						</>
					)}

					{/* VISTA CLASSI */}
					{viewMode === "classes" && (
						<>
							<Tabs id="fm-classes-tabs"
								size="small"
								animated
								items={classTabs}
								activeKey={String(selectedClass)}
								onChange={(key) => setSelectedClass(String(key))}
							/>
							{classLoading && (
								<div className="fm-categories-placeholder">Caricamento classi…</div>
							)}
							{!classLoading && classFilters.length <= 1 && (
								<div className="fm-categories-placeholder">Nessuna classe configurata per questo volantino.</div>
							)}

							<div className="fm-page">
								<div className="fm-grid">{progressiveCardEditors}</div>
								{showProgressiveLoader && (
									<div className="fm-progressive-loader">
										<Spin size="small" />
										<span>{progressiveLoaderText}</span>
									</div>
								)}
							</div>
						</>
					)}

					{/* VISTA FONDI */}
					{viewMode === "funds" && (
						<>
							<Tabs id="fm-funds-tabs"
								size="small"
								animated
								items={fundTabs}
								activeKey={String(selectedFund)}
								onChange={(key) => setSelectedFund(String(key))}
							/>
							{fundLoading && (
								<div className="fm-categories-placeholder">Caricamento fondi…</div>
							)}
							{!fundLoading && fundFilters.length <= 1 && (
								<div className="fm-categories-placeholder">Nessun fondo configurato per questo volantino.</div>
							)}

							<div className="fm-page">
								<div className="fm-grid">{progressiveCardEditors}</div>
								{showProgressiveLoader && (
									<div className="fm-progressive-loader">
										<Spin size="small" />
										<span>{progressiveLoaderText}</span>
									</div>
								)}
							</div>
						</>
					)}

					{/* VISTA BOLLINI */}
					{viewMode === "badges" && (
						<>
							<Tabs id="fm-bollini-tabs"
								size="small"
								animated
								items={badgeTabs}
								activeKey={String(selectedBadge)}
								onChange={(key) => setSelectedBadge(String(key))}
							/>
							{badgeLoading && (
								<div className="fm-categories-placeholder">Caricamento bollini…</div>
							)}
							{!badgeLoading && badgeFilters.length <= 1 && (
								<div className="fm-categories-placeholder">Nessun bollino configurato per questo volantino.</div>
							)}

							<div className="fm-page">
								<div className="fm-grid">{progressiveCardEditors}</div>
								{showProgressiveLoader && (
									<div className="fm-progressive-loader">
										<Spin size="small" />
										<span>{progressiveLoaderText}</span>
									</div>
								)}
							</div>
						</>
					)}

					{viewMode === "filters" && (
						<>
							<Tabs id="fm-filtri-tabs"
								size="small"
								animated
								items={filterTabs}
								activeKey={String(selectedFilter)}
								onChange={(key) => setSelectedFilter(String(key))}
							/>
							{filterLoading && (
								<div className="fm-categories-placeholder">Caricamento filtri…</div>
							)}
							{!filterLoading && filterFilters.length <= 1 && (
								<div className="fm-categories-placeholder">Nessun filtro configurato per questo volantino.</div>
							)}

							<div className="fm-page">
								<div className="fm-grid">{progressiveCardEditors}</div>
								{showProgressiveLoader && (
									<div className="fm-progressive-loader">
										<Spin size="small" />
										<span>{progressiveLoaderText}</span>
									</div>
								)}
							</div>
						</>
					)}

					{viewMode === "mechanics" && (
						<>
							<Tabs id="fm-meccaniche-tabs"
								size="small"
								animated
								items={mechanicTabs}
								activeKey={String(selectedMechanic)}
								onChange={(key) => setSelectedMechanic(String(key))}
							/>
							{mechanicLoading && (
								<div className="fm-categories-placeholder">Caricamento meccaniche…</div>
							)}
							{!mechanicLoading && mechanicFilters.length <= 1 && (
								<div className="fm-categories-placeholder">Nessuna meccanica configurata per questo volantino.</div>
							)}

							<div className="fm-page">
								<div className="fm-grid">{progressiveCardEditors}</div>
								{showProgressiveLoader && (
									<div className="fm-progressive-loader">
										<Spin size="small" />
										<span>{progressiveLoaderText}</span>
									</div>
								)}
							</div>
						</>
					)}
				</div>
			</div>

			<FlyerBulkSelectorModal
				open={bulkModalOpen}
				onClose={() => setBulkModalOpen(false)}
				pages={pages}
				categoryOptions={allCategoryOptions}
				classOptions={allClassOptions}
				fundOptions={allFundOptions}
				badgeOptions={allBadgeOptions}
				filterOptions={filterSelectOptions}
				mechanicOptions={mechanicSelectOptions}
				productFields={productFields}
				genericTaxonomies={genericTaxonomies}
				loading={bulkApplying}
				onApply={handleBulkApply}
				onMove={handleCardMove}
				onDelete={handleBulkDelete}
				getImageUrl={imgUrl}
				onCreateCategory={handleCreateCategory}
				onCreateClass={handleCreateClass}
				onCreateFund={handleCreateFund}
				onCreateBadge={handleCreateBadge}
				onCreateFilter={handleCreateFilter}
				onCreateMechanic={handleCreateMechanic}
			/>

			<UploadRefsModal
				flyerId={flyerId}
				open={refsModalOpen}
				onClose={() => setRefsModalOpen(false)}
				onComplete={() => {
					toast.success?.("Referenze caricate con successo!") || message.success("Referenze caricate con successo!");
					// setRefsModalOpen(false);
				}}
				onRefsCountChange={handleRefsCountChange}
			/>

			<UploadExcelCodesModal
				flyerId={flyerId}
				open={excelModalOpen}
				onClose={() => setExcelModalOpen(false)}
				onOpenCard={(item) => {
					setExcelModalOpen(false);
					navigate(location.pathname, { state: { ...(location.state || {}), flyerName, focusCardId: String(item.cardId) } });
				}}
				onComplete={async () => {
					// La modale resta aperta: mostra il resoconto dell'import.
					try {
						await loadCards();
					}
					catch (error) {
						message.error("Errore nel ricaricamento dei dati");
					}
				}}
			/>

				<UploadPdfModal
					flyerId={flyerId}
					open={pdfModalOpen}
					onClose={() => setPdfModalOpen(false)}
					preserveZones={Array.isArray(rows) && rows.length > 0}
					onComplete={() => {
						setPdfModalOpen(false);
						navigate(flyerData?.isVariant && flyerData?.parentFlyerId ? `/variant-preprocess/${flyerId}` : `/flyerpreprocess/${flyerId}`);
					}}
			/>

			<EditFlyerModal
				open={editModalOpen}
				onClose={() => setEditModalOpen(false)}
				flyer={flyerData}
				onUpdated={handleFlyerUpdated}
			/>
		</>
	);
}
